// src/components/items/ItemForm.jsx
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
	Alert,
	Form,
	App,
	Button,
	Modal,
} from 'antd';
import { api } from '../../api/client';
import AttestationModal from '../users/AttestationModal';
import { needsAttestation } from '../../services/mfa';
import { isStaleItem } from '../../services/items';
import { conflictValueText, sameValue, sortStaleFields } from './staleConflict';
import ItemConflictModal from './ItemConflictModal';
import ItemComments from './ItemComments';

import { API_ITEMS_URL } from '../../utils';
import { cardChain, mergeChainEntries, mergeChainElements, commentsAllowedForChain, commentsExpandedForChain } from '../../utils/cards';
import { getElementInitialValue, getElementValidationErrors, groupElementsIntoPanels, isElementValueEmpty } from '../../utils/elements';
import { needsZone } from '../../utils/elementDefaults';
import { collectionZone, collectionZoneKnown } from '../../utils/collectionTimezone';
import { AUTO_PLACEMENT, formLayoutPanels, leftOutNames, placeRangeEnds } from '../../utils/formLayout';
import { cellSpans } from '../../utils/formGridCells';
import { FormGrid, FormGridCell } from '../common/FormGrid';
import { sectionSummary } from '../../utils/sectionSummary';
import SectionHeaderStatus from './SectionHeaderStatus';
import { lockStateFor, recordPolicyForChain } from '../../utils/recordPolicy';
import useFormLayout from '../../hooks/useFormLayout';
import useWindowClass from '../../hooks/useWindowClass';
import { describeRangeRefusal, rangeStartFor } from '../../utils/dateBounds';
import EventFormElement from '../events/EventFormElement'; // reuse for now
import RangeSpanHint from './RangeSpanHint';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { CHEVRON_RIGHT } from '../icons';
import { useScreenShortcuts } from '../../contexts/ShortcutsContext';
import { validateElementValue } from '../../utils/elementValidation';
import { formatLimit } from '../../utils/elementFormat/text';
import { patternMessageFor } from '../../utils/textRules';
import ItemFormHeader, { ItemFormPaneFooter } from './ItemFormHeader';
import usePaneFormLeave, { paneFormLeaves } from '../../hooks/usePaneFormLeave';
import { useShellSlot } from '../shell/ShellSlot';
import RecordBadge from '../common/RecordBadge';
import { DataState, DATA_STATE, Skeleton, Spinner } from '../loading';
import { describeDuplicateValue, isDuplicateValue } from '../../utils/uniqueValues';
import useHeldFiles, { HeldFilesContext } from '../../hooks/useHeldFiles';
import { fileIdsOf, groupFileIds, isFileElement } from '../../utils/fileValues';
import { withoutConfirmations } from '../../utils/confirmationValues';
import { FILE_REFUSAL_CODES, serverFileRefusal } from '../../utils/fileUpload';
import GlobalLiveNotice from '../collections/GlobalLiveNotice';
import { GlobalDisclaimer } from '../globalCollections/ListingDetails';
import FormActionsFooter from '../common/FormActionsFooter';
import { useItemActions } from './ItemActionsMenu';
import LastItemCard from './LastItemCard';
import RelatedItemsSection from './RelatedItemsSection';
import AttachmentsSection from '../common/AttachmentsSection';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { canTakeCopies } from '../../utils/permissions';
import useCollectionAccess from '../../hooks/useCollectionAccess';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useOptionalNotifications } from '../../contexts/NotificationsContext';
import { getNotifications } from '../../services/notifications';
import { getQuickAddTemplateLists, resolveQuickAddTemplate } from '../../services/quickAddTemplates';
import { recordView } from '../../services/views';
import { createDraft, deleteDraft, getDraft, listDrafts, updateDraft } from '../../services/drafts';
import DraftChips from './DraftChips';
import DraftReplaceModal from './DraftReplaceModal';
import { contentRowText, draftContentRows, draftReplaceRows, losesSomething } from './draftDiff';
import getItemDisplayValue from '../../utils/items';
import { normalizeId } from '../../utils/ids';
import { copyableOnDuplicate } from '../../utils/duplicateCopy';
import { identityElementOf } from '../../utils/userElements';
import { loadMe, refreshMe } from '../../hooks/useMe';
import { isEmptyLookupValue, lookupFamilyHasIdentity, lookupRecordsCollectionId, wantsDefaultToMe, withMe } from '../../utils/lookupMe';
import { describeFilteredOut, isStrictLookup, lookupFilterOf } from '../../utils/lookupFilter';
import { isOffered } from '../../services/lookupOffers';
import { refreshLinkedAccounts } from '../../hooks/useLinkedAccounts';
import { createLookupResolver, omitUnresolvedLookups } from '../../utils/lookups';
import useLinkedItems from '../../hooks/useLinkedItems';
import useEnsureCards from '../../hooks/useEnsureCards';
import ItemInfoCard from './ItemInfoCard';
import { isLockedError } from '../../services/records';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import RecordLockNotice, { lockSentence } from './RecordLockNotice';
import { describeLinkError } from '../../utils/collectionLinks';
import { apiErrorMessage } from '../../utils/apiErrors';
import { planLimitOf, planLimitWords } from '../../utils/planLimit';
import PlanLimitAlert from '../common/PlanLimitAlert';
import { escapeBelongsElsewhere } from '../../utils/escapeOwner';
import ItemFormTags from '../tags/ItemFormTags';
import useActiveTag from '../../hooks/useActiveTag';
import { applyPrivateTags, removePrivateTags } from '../../services/tagApplications';
import { getUsableTags } from '../../services/tags';
import {
	applyTagDelta,
	handTagIdsOf,
	isTagRefusal,
	launchTagsNote,
	ownPrivateTagIdsOf,
	privateDelta,
	privateTagRefusal,
	sameTagIds,
	tagDelta,
	tagDroppedNote,
	tagWriteRefusal,
	withLaunchTags,
} from '../../utils/itemTags';
import { getLockedElements } from '../../services/sharing';
import AccessLockNotice, { GrantReadOnlyNotice } from './AccessLockNotice';
import { isHiddenFromMe, isSeeOnlyElement, withoutHiddenElements } from '../../utils/elementAccess';
import IntakePrivatePanel from './IntakePrivatePanel';
import LinkedEntryNotice from './LinkedEntryNotice';
import { isRepeatingGroup } from '../../config/repeatingGroups';
import {
	ENTRY_COLUMN,
	applyOperation,
	entriesForWire,
	entriesOf,
	entryErrorKey,
	entryKey,
	entryKeyFromRefusal,
	planGroupWrites,
	positionAfter,
	plainRefusal,
	reconcileGroupErrors,
	validateGroupEntries,
	visibleColumns,
	withoutEntryKeys,
} from '../../utils/repeatingGroupEntries';
import { formulaWarningsNotice, formulaWarningsOf, isFormulaTooCostly, mergeFormulaWarnings } from '../../utils/formulaWarnings';
import { sendEntryOperation } from '../../services/itemEntries';


// How long typing must pause before the draft saves.
const DRAFT_DEBOUNCE_MS = 1000;
// The first control a refused field can be fixed in (KPR-581): something
// typeable or pickable, never a hidden input, a disabled one, or a button that
// only opens help. A select's search input is read-only until typed into and
// still the thing to focus.
const FIELD_CONTROLS = 'input, textarea, select, [contenteditable="true"], [role="combobox"], [role="switch"]';
const takesFocus = node => !(
	node.disabled
	|| (node.readOnly && node.getAttribute('role') !== 'combobox')
	|| node.type === 'hidden'
	|| node.getAttribute('aria-disabled') === 'true'
);
const firstFocusable = field => [...field.querySelectorAll(FIELD_CONTROLS)].find(takesFocus) || null;
const NO_CONFLICTS = Object.freeze([]);
const NO_LOCKS = Object.freeze([]);

// A stored item's values, detached from the form's state: the base a stale
// save is judged against (loadedItemRef in ItemForm).
const baseOf = record => ({
	elements: JSON.parse(JSON.stringify(record?.elements || {})),
	// The hand-applied tags as read: an update sends `tagIds` only when the
	// form changed them, and a stale save merges this person's change onto
	// the other person's list (docs/plans/tags.md §5.2).
	tagIds: handTagIdsOf(record),
	updatedAt: record?.updatedAt || null,
});

// What the form keeps beside the item for its tag row and never sends where
// the API would read it: `tagAutoIds` is server-managed (a rule's), `myTags`
// is the reader's own private tags (applied through their own store after
// the save), `tagTitles` is how item tags are named, `warnings` a response's.
const TAG_KEYS_NEVER_SENT = ['tagAutoIds', 'myTags', 'tagTitles', 'warnings'];

// Tags are retired (docs/plans/tags.md §4.1, T1). An item read before the
// owner's clean-up runs may still carry its old `tags` array; the form never
// holds it, so no save can send it back — the API refuses a non-empty
// `tags` with 400 `tags_retired`.
const withoutLegacyTags = (record) => {
	if (!record || typeof record !== 'object') return record || {};
	const { tags: _retiredTags, ...rest } = record;
	return rest;
};

/**
 * The new-item route element. The form is keyed on everything a launcher can
 * change WITHOUT leaving the route — the card, the quick add template, the
 * notification — so a second launch from the context bar's Add item picker
 * (which stays mounted on the form page) mounts a fresh form instead of
 * landing on one whose prefill effects already ran: the one-shot latches
 * (prefillDoneRef, templateDoneRef), the typed-field set and the previous
 * template's values would all otherwise carry over to the new
 * template. A launch is a fresh form; the dirty-form guard still asks before
 * the navigation that gets here. (A duplicate profile's `source` is part of
 * the launch too: the same profile run on another item is another copy.)
 */
export function NewItemFormRoute() {
	const { cardId } = useParams();
	const [searchParams] = useSearchParams();
	const key = `${normalizeId(cardId) || ''}|${searchParams.get('template') || ''}|${searchParams.get('source') || ''}|${searchParams.get('notification') || ''}|${searchParams.get('draft') || ''}`;
	return <ItemForm key={key} itemId="-1" />;
}

/** Is the address bar on an item's short URL (/i/<code>, utils/recordCodes.js)? */
const onShortItemUrl = () => {
	try { return /^\/i\//i.test(window.location.pathname); } catch { return false; }
};

export default function ItemForm(props) {
	// Accept via props or URL
	const routeParams = useParams();
	const collectionId = props.collectionId || routeParams.collectionId;
	const itemId = props.itemId || routeParams.itemId || '-1';
	const cardIdParam = props.cardId || routeParams.cardId;
	// The list's detail pane hosts this form too (KPR-1644): `variant="pane"`
	// with onSaved / onCancel / onDeleted and the pane's two slots.
	const pane = props.variant === 'pane' ? props : null;
	// A notification CTA lands here as ?notification=<id>; the prefill payload
	// itself stays on the notification row (see the effect below).
	const [searchParams] = useSearchParams();
	const notificationId = searchParams.get('notification');
	// A quick add launcher lands here as ?template=<id>; the form resolves it
	// on the API itself (reload-safe), and the launcher's navigation state
	// carries only the name, for the header line.
	const templateId = searchParams.get('template');
	// A parent collection's template for a sub-collection's card opens the
	// form in the sub-collection (where the record is made) and says where
	// the template is defined: `?templateIn=<parentId>` — resolved there.
	const templateCollectionId = (templateId && normalizeId(searchParams.get('templateIn'))) || collectionId;
	// A DUPLICATE PROFILE launch (Duplicate ▾ — utils/duplicateProfiles.js)
	// is a template launch plus `?source=<itemId>`, the item being copied:
	// the API resolves the profile against it, under the caller's grants.
	const sourceItemId = templateId ? normalizeId(searchParams.get('source')) : '';
	const isProfileLaunch = !!sourceItemId;
	// "Duplicate" on the detail pane lands here with the source item's
	// element values in navigation state (ItemActionsMenu duplicateItemNavigation).
	const location = useLocation();
	const duplicatePrefill = location.state?.prefill || null;
	const duplicateElements = duplicatePrefill?.elements || null;
	const quickAddState = location.state?.quickAdd || null;

	const isNewRecord = (itemId + '') === '-1';

	// Drafts (keepr-api SCHEMA.md § Item drafts) live on the server, private
	// to their author — never in localStorage, which outlives sign-out on a
	// shared device. New items only; an existing item's source of truth is
	// the item. A quick add launch has NO draft: its values are re-resolvable,
	// and typing on top of them must not turn up as a chip on the next plain
	// "Add item" of the card. The form always opens BLANK; this person's
	// drafts of the card are offered as chips above it, and `?draft=<id>`
	// (the Drafts page's Open) applies one on arrival.
	const draftsEnabled = isNewRecord && Boolean(collectionId) && Boolean(cardIdParam) && !templateId;
	const draftParamId = draftsEnabled ? normalizeId(searchParams.get('draft')) : '';
	// 'idle' | 'saved' | 'failed' — drives the header's draft note.
	const [draftStatus, setDraftStatus] = useState('idle');
	// Set by typing (a field): only what the person wrote arms
	// the autosave below, never a prefill or a reseeded repeat form.
	const draftWorthyRef = useRef(false);
	// Set when a save came back 428: the card wants this record signed, and
	// the modal collects the signature for the retry.
	const [attestation, setAttestation] = useState(null);
	const [item, setItem] = useState({
		_id: undefined,
		collection_id: collectionId || '',
		card_id: cardIdParam || '',
		elements: {},
	});

	// The reader's private tags on the item as the form read it (its
	// `myTags`): what Save compares against to know which private
	// applications to add and take off once the item is written.
	const loadedMyTagsRef = useRef([]);
	// The tag row's picker, opened from outside by the ⋯ "Tag…".
	const [tagPickerOpen, setTagPickerOpen] = useState(false);
	const tagRowRef = useRef(null);
	// What a quick add, a duplicate or a draft LEFT OFF of the tags it carried
	// (T9 — utils/itemTags.js launchTagsNote), said beside the tag row.
	const [launchTagNote, setLaunchTagNote] = useState(null);
	// The item AS THIS FORM READ IT — its element values, tags and
	// `updatedAt` — the base a stale save is judged against (delete audit
	// F6). An update sends `ifUpdatedAt` from here; a 409 `stale_item`
	// compares the form and the item as it stands now against it, field by
	// field (staleConflict.sortStaleFields). It moves when a save is SENT
	// against a newer version, and when one lands.
	const loadedItemRef = useRef(null);
	// The open "Someone changed this item" dialog: { conflicts, onSave }.
	const [staleConflict, setStaleConflict] = useState(null);

	const [itemLoading, setItemLoading] = useState(!isNewRecord);
	// The read of the item failed (not "is loading"): the form says so, with Retry.
	const [itemError, setItemError] = useState(false);
	const [itemAttempt, setItemAttempt] = useState(0);
	// The person's Refresh (KPR-1346): the item read again in place, with no
	// skeleton. `itemRefreshing` while it runs; `itemGone` once a re-read
	// says the item was deleted or is no longer readable (the form is not
	// shown then); `refreshFailed` when it failed for any other reason (the
	// form stays as it was, and says so with Retry).
	const [itemRefreshing, setItemRefreshing] = useState(false);
	const [itemGone, setItemGone] = useState(false);
	const [refreshFailed, setRefreshFailed] = useState(false);
	const itemRefreshRef = useRef(null);
	// The write in flight: null, 'save' or 'another' (Save & add another), so the
	// control that was pressed is the one that shows it. `saveFailed` is the last
	// attempt having been refused or having failed: the form still holds the input.
	const [saving, setSaving] = useState(null);
	const [saveFailed, setSaveFailed] = useState(false);
	// A save refused at a plan limit (409 `plan_limit`): said once above the
	// form in the PlanLimit pattern until the next save (standards §7).
	const [planLimit, setPlanLimit] = useState(null);
	// A save refused because the record is locked (409 `item_locked`): the
	// API's sentence, held until the record is unlocked or read again. It
	// closes the form even where this side still thought the record open
	// (the grace period or an unlock ran out while it was being edited).
	const [lockRefusal, setLockRefusal] = useState(null);
	// Bumped when the lock's next boundary passes (a grace period or an
	// unlock ending), so the form closes on time without a save to find out.
	const [lockClock, setLockClock] = useState(0);
	const lastAddAnotherRef = useRef(false);
	// Bumped when the ⋯ menu relates something, so the Related section below
	// the form reloads in place.
	const [relatedRefreshKey, setRelatedRefreshKey] = useState(0);
	const [validationErrors, setValidationErrors] = useState({});

	// A blocked navigation on a new item with a draft behind it gets the
	// same Keep as draft / Discard prompt as Cancel (set further down, once
	// the draft machinery exists).
	const onLeaveBlockedRef = useRef(null);
	const { isDirty, resetDirty, guardDiscard } = useDirtyForm(item, {
		// A new-item form is keyed on its launch (template, source,
		// notification, draft — NewItemFormRoute), so a change of query
		// there mounts a fresh form: that is leaving, too.
		// In the pane, the list moving away from the item is leaving too.
		shouldBlock: pane ? paneFormLeaves : ({ currentLocation, nextLocation }) => (
			currentLocation.pathname !== nextLocation.pathname
			|| (isNewRecord && currentLocation.search !== nextLocation.search)
		),
		onBlocked: args => (onLeaveBlockedRef.current ? onLeaveBlockedRef.current(args) : false),
	});
	const autoResetAllowedRef = useRef(true);
	const resetDirtyRef = useRef(resetDirty);

	useEffect(() => {
		resetDirtyRef.current = resetDirty;
	}, [resetDirty]);

	const markPristine = useCallback((nextSnapshot) => {
		autoResetAllowedRef.current = true;
		resetDirtyRef.current(nextSnapshot);
	}, []);

	const markPristineIfAllowed = useCallback((nextSnapshot) => {
		if (!autoResetAllowedRef.current) return;
		markPristine(nextSnapshot);
	}, [markPristine]);

	const markDirty = useCallback(() => {
		autoResetAllowedRef.current = false;
	}, []);
	const navigate = useNavigate();
	const { message } = App.useApp();
	const collectionContext = useCollectionContext();
	const collectionAccess = useCollectionAccess();
	const collection = collectionContext?.collection;
	const collectionRecordId = collection?._id;
	// A file element's uploads before the save (hooks/useHeldFiles, PR 10b):
	// claimed by the save, deleted by Cancel, Discard or leaving an edit,
	// kept by a draft. Whether the collection takes files at all: null until
	// the collection is known (the input shows; the server has the last word).
	const heldFiles = useHeldFiles({ collectionId });
	const { seed: seedHeldFiles, settle: settleHeldFiles } = heldFiles;
	const attachmentsOn = collection ? collection.allowAttachments === true : null;
	// The zone a new item's today / now / relative default is read in: the
	// COLLECTION's, as the server reads it (the owner's ruling B11) — the
	// GET's resolved answer, so a sub-collection's inherited zone and keepr's
	// default both arrive already settled.
	const zone = collectionZone(collection);
	const zoneKnown = collectionZoneKnown(collectionContext);
	const {
		cardsById,
		elementSetsById,
		getElementsForCard,
		loading: cardDefinitionsLoading,
		loaded: cardDefinitionsLoaded,
		isCardMissed,
	} = useCardDefinitions();

	// Load existing item (edit)
	useEffect(() => {
		if (!isNewRecord) {
			setItemLoading(true);
			setItemError(false);
			api.get(`${API_ITEMS_URL}/${itemId}`).then(({ data: loadedItem, status }) => {
				if (status !== 200) return;
				// An item's short URL (/i/<code>) names no collection, and items
				// move: when this browser remembered the collection it lived in
				// before a move, the item says where it lives now (KPR-200).
				// Only for the short address — a long one names its collection
				// on purpose.
				if (onShortItemUrl() && routeParams.collectionId && loadedItem?.collection_id
					&& normalizeId(loadedItem.collection_id) !== normalizeId(routeParams.collectionId)) {
					navigate(`/collections/${normalizeId(loadedItem.collection_id)}/items/${itemId}${window.location.search}${window.location.hash}`, { replace: true });
					return;
				}
				const sanitizedItem = withoutLegacyTags(loadedItem);
				loadedMyTagsRef.current = Array.isArray(sanitizedItem?.myTags) ? sanitizedItem.myTags : [];
				loadedItemRef.current = baseOf(sanitizedItem);
				setItem(sanitizedItem);
				markPristine(sanitizedItem);
				recordView('item', itemId);
			}).catch(() => {
				setItemError(true);
			}).finally(() => {
				setItemLoading(false);
			});
		}
	}, [isNewRecord, itemId, message, markPristine, routeParams.collectionId, navigate, itemAttempt]);

	// Refresh (KPR-1346). Clean: read the item again and show it in place —
	// its values, tags and Related section — as the stored copy the next save
	// is checked against. Unsaved edits: the form's own discard question
	// first (Keep editing / Discard); nothing is thrown away without it, and
	// Discard settles the uploads the edit held, as leaving does. One read at
	// a time; an answer for another item (the address moved on) is dropped.
	const currentItemIdRef = useRef(itemId);
	currentItemIdRef.current = itemId;
	const readItemAgain = useCallback(() => {
		if (isNewRecord || itemRefreshRef.current) return;
		const askedFor = itemId;
		itemRefreshRef.current = askedFor;
		setItemRefreshing(true);
		setRefreshFailed(false);
		api.get(`${API_ITEMS_URL}/${askedFor}`).then(({ data: loadedItem }) => {
			if (currentItemIdRef.current !== askedFor) return;
			const sanitizedItem = withoutLegacyTags(loadedItem);
			loadedMyTagsRef.current = Array.isArray(sanitizedItem?.myTags) ? sanitizedItem.myTags : [];
			loadedItemRef.current = baseOf(sanitizedItem);
			setItem(sanitizedItem);
			markPristine(sanitizedItem);
			setItemGone(false);
			// The stored copy now says whether the record is open.
			setLockRefusal(null);
			setRelatedRefreshKey(key => key + 1);
		}).catch((error) => {
			if (currentItemIdRef.current !== askedFor) return;
			const status = error?.response?.status;
			if (status === 404 || status === 403) setItemGone(true);
			else setRefreshFailed(true);
		}).finally(() => {
			if (itemRefreshRef.current !== askedFor) return;
			itemRefreshRef.current = null;
			setItemRefreshing(false);
		});
	}, [isNewRecord, itemId, markPristine]);
	const handleRefresh = useCallback(() => {
		if (isNewRecord || itemRefreshRef.current) return;
		const hadEdits = isDirty;
		guardDiscard(() => {
			if (hadEdits) settleHeldFiles([]);
			readItemAgain();
		}, {
			content: 'Refresh loads the latest copy of this item, and your unsaved changes would be lost.',
			okText: 'Discard and refresh',
		});
	}, [isNewRecord, isDirty, guardDiscard, readItemAgain, settleHeldFiles]);
	// A new address is a new item: nothing of the last one's refresh stays.
	useEffect(() => {
		setItemGone(false);
		setRefreshFailed(false);
		setLockRefusal(null);
	}, [itemId]);

	// Sync collection_id with the layout-provided collection record
	useEffect(() => {
		if (!collectionRecordId) return;
		setItem(prev => {
			if (prev.collection_id) return prev;
			const next = { ...prev, collection_id: collectionRecordId };
			markPristineIfAllowed(next);
			return next;
		});
	}, [collectionRecordId, markPristineIfAllowed]);

	// Resolve card id to use (URL-provided for "new", or from the item when editing)
	const effectiveCardId = useMemo(() => {
		return item.card_id || cardIdParam || '';
	}, [item.card_id, cardIdParam]);

	// The card definition hierarchy (parent first, child last) read from the
	// app-wide cache — no per-level GET. Its elements merge by the platform's
	// one rule (utils/cards): a child's re-declaration of an inherited name
	// renders once, as the child defines it, in the ancestor's position. Each
	// node comes resolved with its element sets folded in, so a set's fields
	// render in a panel named for the set like any grouped element.
	const cardDefChain = useMemo(() => cardChain(cardsById, effectiveCardId, elementSetsById || null), [cardsById, effectiveCardId, elementSetsById]);
	const cardDef = cardDefChain.length ? cardDefChain[cardDefChain.length - 1] : null;
	const cardDefId = normalizeId(cardDef?._id) || null;
	// The card's FORM layout (utils/formLayout): the order and groups its
	// elements render in, inherited from the nearest card up the chain that
	// has one. Asked for as soon as the card id is known, in parallel with
	// the item; the fields wait for it (below) so a form never re-orders
	// under somebody already typing into it — but only for so long
	// (useFormLayout FORM_LAYOUT_WAIT_MS): a layout that has not answered by
	// then renders automatic, and swaps in on arrival only if nobody has
	// touched the form yet. Once they have, this open keeps the order they
	// are looking at; the next open reads the layout from the cache.
	const { layout: liveFormLayout, loaded: formLayoutLoaded } = useFormLayout(effectiveCardId);
	// The card the person has put a hand on the form FOR — not a boolean
	// reset when the held layout first syncs: that sync is a passive effect,
	// and it can run after the first keystroke, which then went unnoticed.
	const cardIdRef = useRef(effectiveCardId);
	cardIdRef.current = effectiveCardId;
	const interactedForRef = useRef(null);
	const noteInteraction = useCallback(() => { interactedForRef.current = cardIdRef.current; }, []);
	const [heldFormLayout, setHeldFormLayout] = useState({ cardId: null, layout: null });
	useEffect(() => {
		if (!formLayoutLoaded) return;
		setHeldFormLayout(prev => {
			if (prev.cardId === effectiveCardId && (prev.layout === liveFormLayout || interactedForRef.current === effectiveCardId)) return prev;
			return { cardId: effectiveCardId, layout: liveFormLayout };
		});
	}, [effectiveCardId, liveFormLayout, formLayoutLoaded]);
	const formLayout = heldFormLayout.cardId === effectiveCardId ? heldFormLayout.layout : liveFormLayout;
	// An element whose values a rule hides from this person (`access:
	// 'hidden'`, served only because they edit the card — KPR-509) is not on
	// their form: the values never arrive, and an empty field would read as
	// "not set" and invite a value the API drops.
	const allElementDefs = useMemo(() => withoutHiddenElements(mergeChainElements(cardDefChain)), [cardDefChain]);
	// Every file id the form's file elements hold (PR 10b), and the file
	// columns of its repeating groups' entries (KPR-934): what a save claims,
	// and what a draft kept on leaving keeps.
	const fileIdsIn = useCallback(elements => allElementDefs.flatMap((def) => {
		if (isFileElement(def)) return fileIdsOf(elements?.[def.name]);
		if (isRepeatingGroup(def)) return groupFileIds(def, elements?.[def.name]);
		return [];
	}), [allElementDefs]);
	// The card whose definition has seeded the element keys below. The form
	// stays behind the spinner until then, as it did while the definition was
	// fetched: a field that mounted before its key existed would see its
	// value flip from undefined to the seed and drop whatever was typed in
	// between (DateField ends an edit whenever its value changes).
	const [seededCardId, setSeededCardId] = useState(null);
	// Counts "Save & add another" resets: a repeating group's "Removed … Undo" belongs to the item it was on.
	const [repeatEpoch, setRepeatEpoch] = useState(0);
	const cardReady = !!cardDefId && seededCardId === cardDefId;
	// The cache is an authority on what it does NOT hold only once a fetch has
	// SUCCEEDED. A cache whose last fetch failed (the login page's pre-token
	// fetch, a 429) reads as settled and empty, and it is this very mount
	// that retries it (ensureLoaded) — so that first render is not a verdict.
	// It becomes one once the retry has been seen to run: from then on a
	// settled cache that still lacks the card has failed, as a 404 from the
	// per-id fetch once did.
	const [cacheRetried, setCacheRetried] = useState(false);
	useEffect(() => {
		if (cardDefinitionsLoading) setCacheRetried(true);
	}, [cardDefinitionsLoading]);
	const cacheSettled = !cardDefinitionsLoading && (cardDefinitionsLoaded || cacheRetried);
	// A card the loaded list does not hold is not missing YET: the app-wide
	// list carries only the reader's own collections' cards, and this item's
	// may be one it reads some other way — a record opened in the collection
	// a link reads it from (a borrowed card), a global collection's. The
	// cache fetches such a card by id (useEnsureCards → noteMissingCards);
	// the form waits for that verdict. A cache whose list fetch failed has
	// no on-demand refetch to wait for, so there a settled miss is the
	// verdict, as before.
	useEnsureCards(effectiveCardId || null);
	const cardVerdictIn = !cardDefinitionsLoaded || typeof isCardMissed !== 'function' || isCardMissed(effectiveCardId);
	const cardMissing = !!effectiveCardId && !cardDef && cacheSettled && cardVerdictIn;
	// Loading until the definition has seeded the form, or is known missing.
	const cardLoading = !!effectiveCardId && !cardReady && !cardMissing;
	const cardMissingReportedRef = useRef(null);
	useEffect(() => {
		if (!cardMissing || cardMissingReportedRef.current === effectiveCardId) return;
		cardMissingReportedRef.current = effectiveCardId;
		message.error('Failed to load card definition.');
	}, [cardMissing, effectiveCardId, message]);

	// Fill the element keys once the definition is in hand. Re-runs only when
	// the cache changes (a refresh); a key already present is left alone.
	useEffect(() => {
		if (!cardDef) return;
		// A default that reads a clock is seeded once, in the collection's zone:
		// wait for the collection rather than seed keepr's default and keep it.
		if (isNewRecord && !zoneKnown && allElementDefs.some(needsZone)) return;
		let usedPrefill = false;
		setItem(prev => {
			const nextElements = { ...(prev.elements || {}) };
			allElementDefs.forEach(el => {
				const name = el?.name;
				if (name && !Object.prototype.hasOwnProperty.call(nextElements, name)) {
					// A resolved notification prefill outranks the
					// card-def default — the rule author chose the
					// value; the default is only a convenience.
					// A plain Duplicate leaves an element no copy may carry
					// (utils/duplicateCopy.js — the account link, a driven or
					// numbered element, one marked "Leave blank when
					// duplicating") to its card default, like a new item: the
					// original's value is the one thing that must not be
					// carried. The API's copyAll profile applies the same rule.
					const blankedCopy = prefillIsDuplicateRef.current && !copyableOnDuplicate(el);
					if (!blankedCopy && prefillRef.current && Object.prototype.hasOwnProperty.call(prefillRef.current, name)) {
						nextElements[name] = prefillRef.current[name];
						usedPrefill = true;
					} else {
						nextElements[name] = getElementInitialValue(el, { isNewRecord, zone });
						defaultSeededRef.current.add(name);
					}
				}
			});
			const childId = cardDef?._id;
			const next = {
				...prev,
				card_id: prev.card_id || childId || prev.card_id,
				elements: nextElements,
			};
			if (!usedPrefill) markPristineIfAllowed(next);
			return next;
		});
		if (usedPrefill) markDirty();
		setSeededCardId(normalizeId(cardDef._id) || null);
	}, [cardDef, allElementDefs, isNewRecord, markPristineIfAllowed, markDirty, zone, zoneKnown]);

	// The inbox, when there is one. Read through a ref so a 60-second poll
	// (which replaces the array identity) can never re-run the prefill effect.
	const notificationsInbox = useOptionalNotifications();
	const inboxRowsRef = useRef(null);
	inboxRowsRef.current = notificationsInbox?.notifications;
	const prefillDoneRef = useRef(false);
	// The prefill race, both directions: the card-def effect and the prefill
	// effect resolve in either order (context hit = sync-ish; list-fallback =
	// a full round trip). prefillRef lets whichever runs SECOND still honor
	// the author's values; defaultSeededRef records which names hold nothing
	// but a card-def default (defaultToToday, defaultValue, []) so prefill may
	// overwrite them; userTouchedRef records real typing, which nothing
	// overwrites.
	// Duplicate-prefill is known synchronously at mount, so it seeds the ref
	// directly; the card-def effect then prefers it over card defaults the
	// same way it prefers a notification prefill. Precedence: notification
	// (async, latches later) > quick add template (async, same) > duplicate
	// > card default. An explicit launch — a notification CTA, a quick add —
	// outranks whatever was lying around, so the duplicate payload does not
	// seed when one is in the URL. A DRAFT is not in this ladder: the form
	// opens without one, and applying one (a chip, `?draft=`) is the
	// person's own act, which overwrites whatever the form holds.
	const prefillRef = useRef((() => {
		if (!isNewRecord) return null;
		if (notificationId || templateId) return null;
		if (duplicateElements) return { ...duplicateElements };
		return null;
	})());
	// Whether prefillRef holds a plain Duplicate's copy (rather than a
	// resolved notification or template, which later replace it): only a
	// copy honours "Leave blank when duplicating" — a profile applies it on
	// the API, and a notification or template is somebody's explicit value.
	const prefillIsDuplicateRef = useRef(!!(isNewRecord && !notificationId && !templateId && duplicateElements));
	// A plain Duplicate of an item holding files (PR 10b): the copy is seeded
	// with the source's file ids (the server binds each to the new item on the
	// same bytes when it is created), and the form shows them from the SOURCE
	// item's thumbnails — the navigation keeps that item's id and its `files`
	// facts beside the values (ItemActionsMenu duplicateItemNavigation). A file
	// element left blank on duplicating (G5's default) never gets one.
	const duplicateFileSource = prefillIsDuplicateRef.current ? (duplicatePrefill?.fileSource || null) : null;
	useEffect(() => {
		if (!duplicateFileSource?.itemId) return;
		seedHeldFiles(duplicateFileSource.files, { itemId: normalizeId(duplicateFileSource.itemId) });
	}, [duplicateFileSource, seedHeldFiles]);
	const defaultSeededRef = useRef(new Set());
	const userTouchedRef = useRef(new Set());
	// The names a notification CTA or a quick add PREFILLED over a card
	// default (the prefill landing after the card-def effect seeded). They
	// stay in defaultSeededRef — a later prefill may still overwrite them, and
	// "default to me" still never fills a non-empty one — but they are the
	// author's values, not defaults: a draft applied over them must say it
	// replaces them (requestApplyDraft's `seeded` leaves them out). Before the
	// review of the live-pass merges they counted as defaults, so whether a
	// draft silently discarded a prefill depended on which effect ran first.
	const prefilledRef = useRef(new Set());

	/**
	 * Notification prefill. A `create-item` CTA on a notification carries the
	 * element values the rule author wanted seeded ("resident: suzy"); the CTA
	 * link carries only the notification id, so the values are read from the
	 * row here rather than from the URL — rich-text prefills would blow past URL
	 * limits, and URLs leak into logs.
	 *
	 * Three rules, all deliberate:
	 * - NEW items only. Seeding an existing item would silently rewrite stored
	 *   data because someone followed a link.
	 * - The user always wins. Only keys the form has not got a value for are
	 *   filled, and the effect bails entirely once the user has typed.
	 * - A missing, foreign, or stale notification id degrades to a plain
	 *   unseeded form. Nothing about it is worth an error toast.
	 */
	useEffect(() => {
		if (!isNewRecord || !notificationId || prefillDoneRef.current) return undefined;

		let active = true;
		const targetCardId = normalizeId(cardIdParam);
		const findRow = rows => (Array.isArray(rows) ? rows : [])
			.find(row => normalizeId(row?._id) === notificationId);

		(async () => {
			// Context first; the list endpoint is the fallback because the API
			// has no single-notification GET (the list IS the read surface).
			// `state: 'all'` because a prefill must not depend on which TAB the
			// drawer was left on — a resolved notification's "Log it" link is
			// still a link somebody just followed.
			let row = findRow(inboxRowsRef.current);
			if (!row) {
				try {
					const { rows } = await getNotifications({ state: 'all', limit: 100 });
					row = findRow(rows);
				}
				catch {
					return;
				}
			}
			if (!active || !row) return;

			const cta = (Array.isArray(row.ctas) ? row.ctas : []).find(entry => (
				entry?.type === 'create-item'
				&& (!targetCardId || normalizeId(entry.card_id) === targetCardId)
				// Collection must match too: a hand-edited URL must not carry
				// collection B's prefill into collection A's form for a card
				// they happen to share.
				&& (!entry.collection_id || !collectionId || normalizeId(entry.collection_id) === normalizeId(collectionId))
			));
			const prefill = cta?.prefill;
			if (!prefill || !Object.keys(prefill).length) return;

			// Latched only once the seed actually lands, so StrictMode's
			// mount/cleanup/mount cycle cannot swallow the one real pass.
			if (prefillDoneRef.current) return;
			prefillDoneRef.current = true;

			// Latch the payload so the card-def effect, when IT runs second,
			// seeds these values instead of the card defaults.
			prefillRef.current = prefill;
			prefillIsDuplicateRef.current = false;

			// The seeded form is a CHANGE: leaving it pristine would make the
			// Save button a no-op for a form the user never had to touch.
			markDirty();
			setItem(prev => {
				const nextElements = { ...(prev.elements || {}) };
				Object.entries(prefill).forEach(([name, value]) => {
					const current = nextElements[name];
					// "Untouched" includes a value the card-def effect seeded as
					// a DEFAULT (defaultToToday, defaultValue, []) — the author's
					// prefill outranks a default, never real typing.
					const untouched = !userTouchedRef.current.has(name)
						&& (current === undefined || current === null || current === ''
							|| defaultSeededRef.current.has(name));
					if (untouched) {
						nextElements[name] = value;
						prefilledRef.current.add(name);
					}
				});
				return { ...prev, elements: nextElements };
			});
		})();

		return () => { active = false; };
	}, [isNewRecord, notificationId, cardIdParam, collectionId, markDirty]);

	/**
	 * Quick add template prefill. The launcher lands here with
	 * ?template=<id>; the form asks the API to resolve it (statics copied,
	 * formulas evaluated under the CALLER's own grants — `{{last.x}}` reveals
	 * nothing the caller could not list) and seeds the result exactly as a
	 * notification prefill is seeded: new items only, untouched keys only, a
	 * missing or refused template degrades to a plain form.
	 */
	const templateDoneRef = useRef(false);
	const [quickAddName, setQuickAddName] = useState(quickAddState?.name || '');

	/**
	 * TEMPLATES CARRY TAGS (docs/plans/tags.md §5.2, T9). A launch's tags —
	 * `{ tagIds, privateTagIds, dropped }`, judged by the API for THIS person
	 * where the item is made — go on the form's tag row as any tag picked
	 * there does: hand tags ride the create, private tags go through the
	 * person's own store once the item exists (the save below). Nothing is
	 * applied that the person can't see before Save; what was left off is said
	 * beside the row. A seeded form is a change, so Save works untouched.
	 */
	const applyLaunchTags = useCallback((launch, source) => {
		// (An API from before T9 answered a template's `tags` as the retired
		// strings — an array: nothing lands from that.)
		if (!launch || typeof launch !== 'object' || Array.isArray(launch)) return;
		const lands = (launch.tagIds?.length || 0) + (launch.privateTagIds?.length || 0);
		if (lands) {
			markDirty();
			setItem(prev => ({ ...prev, ...withLaunchTags(prev, launch) }));
		}
		setLaunchTagNote(launchTagsNote(launch.dropped, source));
	}, [markDirty]);

	/**
	 * THE ACTIVE TAG (docs/plans/tags-active-tag-proposal.md; build calls
	 * b46–b52). A NEW item's form — Add item, a quick add, a duplicate or a
	 * duplicate profile, a draft — puts the person's active tag on its Tags
	 * row as one of their private tags, through the launch path above
	 * (`withLaunchTags`): shown before Save, marked "Your active tag", its ✕
	 * leaving it off this one item, applied through the person's own store
	 * after the create like every private tag here. NEVER on an edit (the
	 * server never applies it either). Once per form, when the answer is final
	 * (`ready`); it is not a change the person made, so a form holding nothing
	 * else is still pristine — leaving it asks nothing.
	 */
	const { activeTag, ready: activeTagReady } = useActiveTag();
	const activeTagDoneRef = useRef(false);
	// The active tag this form put on (null: none was on when it opened). Its
	// chip is marked only while it is still the active tag; turned off in the
	// meantime (the shell's ✕), it stays on the row as a plain private tag —
	// turning it off takes it off nothing, and the person sees what lands.
	const [activeTagOnForm, setActiveTagOnForm] = useState(null);
	const activeTagOnFormRef = useRef(null);
	useEffect(() => {
		if (!isNewRecord || activeTagDoneRef.current || !activeTagReady) return;
		activeTagDoneRef.current = true;
		const id = activeTag?.tagId || null;
		if (!id) return;
		activeTagOnFormRef.current = id;
		setActiveTagOnForm(id);
		setItem(prev => {
			const next = { ...prev, ...withLaunchTags(prev, { privateTagIds: [id] }) };
			markPristineIfAllowed(next);
			return next;
		});
	}, [isNewRecord, activeTagReady, activeTag, markPristineIfAllowed]);

	/** One toast for every warning the resolve returned, deduped. */
	const reportTemplateWarnings = useCallback((warnings) => {
		const texts = [...new Set((Array.isArray(warnings) ? warnings : [])
			.map(w => (typeof w === 'string' ? w : (w?.element ? `${w.element}: ${w.message}` : w?.message)))
			.filter(Boolean))];
		if (texts.length) message.warning(`${isProfileLaunch ? 'Duplicate' : 'Quick add'}: ${texts.join(' · ')}`);
	}, [message, isProfileLaunch]);

	/**
	 * The launch's resolve: a plain template by id; a duplicate profile with
	 * the item it copies from.
	 */
	const resolveLaunch = useCallback(() => (sourceItemId
		? resolveQuickAddTemplate(collectionId, templateId, { sourceItemId })
		: resolveQuickAddTemplate(templateCollectionId, templateId)
	), [collectionId, templateCollectionId, templateId, sourceItemId]);

	/**
	 * A refused resolve. A missing or foreign template (403/404) and a
	 * template the server can no longer apply (400) each get one quiet
	 * warning; anything else (a 500, the network) says the fill did not
	 * happen — the header still names the template, so a silent blank form
	 * would read as "this quick add sets nothing".
	 */
	const reportTemplateFailure = useCallback((error) => {
		const status = error?.response?.status;
		const code = error?.response?.data?.code;
		// A profile's source problems (the item is not readable any more, is
		// of another card, or was not given) are the API's own words: they
		// say what happened to the ITEM, not to the profile.
		if (code === 'source_unavailable' || code === 'source_card_mismatch' || code === 'source_required') {
			message.warning(error?.response?.data?.message || 'That item cannot be duplicated with this profile.');
		} else if (status === 403 || status === 404) message.warning(isProfileLaunch ? 'That duplicate profile is no longer available.' : 'That quick add is no longer available.');
		else if (status === 400) message.warning(error?.response?.data?.message || 'That quick add is no longer available.');
		else message.warning(isProfileLaunch ? 'The duplicate could not be filled in — the form is blank.' : 'The quick add could not be filled in — the form is blank.');
	}, [message, isProfileLaunch]);

	useEffect(() => {
		if (!isNewRecord || !templateId || !collectionId || templateDoneRef.current) return undefined;

		let active = true;
		(async () => {
			let resolved;
			try {
				resolved = await resolveLaunch();
			} catch (error) {
				if (active) reportTemplateFailure(error);
				return;
			}
			if (!active) return;
			// Latched only once the seed actually lands, so StrictMode's
			// mount/cleanup/mount cycle cannot swallow the one real pass.
			if (templateDoneRef.current) return;
			templateDoneRef.current = true;

			const elements = resolved?.elements && typeof resolved.elements === 'object' ? resolved.elements : {};
			reportTemplateWarnings(resolved?.warnings);
			// Its tags (T9): what the API says lands for this person here goes
			// on the form's tag row, shown before Save; what it left off is said.
			applyLaunchTags(resolved?.tags, isProfileLaunch ? 'the duplicate profile' : 'the quick add');

			if (Object.keys(elements).length) {
				// Latch the payload so the card-def effect, when IT runs
				// second, seeds these values instead of the card defaults.
				prefillRef.current = elements;
				prefillIsDuplicateRef.current = false;
				// The seeded form is a CHANGE: Save must work without the
				// user having to touch a field.
				markDirty();
				setItem(prev => {
					const nextElements = { ...(prev.elements || {}) };
					Object.entries(elements).forEach(([name, value]) => {
						const current = nextElements[name];
						// "Untouched" includes a value the card-def effect seeded
						// as a DEFAULT — the template outranks a default, never
						// real typing.
						const untouched = !userTouchedRef.current.has(name)
							&& (current === undefined || current === null || current === ''
								|| defaultSeededRef.current.has(name));
						if (untouched) {
							nextElements[name] = value;
							prefilledRef.current.add(name);
						}
					});
					return { ...prev, elements: nextElements };
				});
			}

			// The header names the template. The launcher passed the name in
			// navigation state; a pasted link or a reload has to look it up.
			if (!quickAddState?.name) {
				try {
					// The templates of the collection that defines it (`templateIn`).
					const { templates } = await getQuickAddTemplateLists(templateCollectionId);
					const match = templates.find(row => normalizeId(row?._id) === normalizeId(templateId));
					if (active && match?.name) setQuickAddName(match.name);
				} catch { /* the line simply reads "Quick add" */ }
			}
		})();

		return () => { active = false; };
	}, [isNewRecord, templateId, collectionId, templateCollectionId, quickAddState?.name, markDirty, reportTemplateWarnings, reportTemplateFailure, resolveLaunch, applyLaunchTags, isProfileLaunch]);

	/**
	 * A plain Duplicate's tags (T9): the source's hand tags and this person's
	 * own private tags on it came in navigation state. Asked of the API first
	 * (GET /api/tags/usable) — only what this person can put on here goes on
	 * the form, and what can't is said. New items only; once.
	 */
	const duplicateTagsDoneRef = useRef(false);
	useEffect(() => {
		if (!isNewRecord || templateId || notificationId || !collectionId || duplicateTagsDoneRef.current) return undefined;
		const ids = [...(duplicatePrefill?.tagIds || []), ...(duplicatePrefill?.privateTagIds || [])];
		if (!ids.length) return undefined;
		let active = true;
		// Latched only once it lands (StrictMode's mount/cleanup/mount).
		getUsableTags(collectionId, ids)
			.then(launch => {
				if (!active || duplicateTagsDoneRef.current) return;
				duplicateTagsDoneRef.current = true;
				applyLaunchTags(launch, 'the item you duplicated');
			})
			.catch(() => {
				if (!active || duplicateTagsDoneRef.current) return;
				duplicateTagsDoneRef.current = true;
				setLaunchTagNote('The tags of the item you duplicated could not be checked, so none were put on. Add them from the Tags row.');
			});
		return () => { active = false; };
	}, [isNewRecord, templateId, notificationId, collectionId, duplicatePrefill, applyLaunchTags]);

	/**
	 * `options.defaultToMe` on a card-lookup (utils/lookupMe.js): a new item
	 * opens with the signed-in person's own record of the lookup's card —
	 * the Property log's Staff field already naming the staff member who is
	 * logging. It is a CARD DEFAULT, so it sits at the bottom of the
	 * precedence (notification > template > duplicate > draft > card
	 * default) without a rule of its own: it only ever fills a field that
	 * still holds nothing but the card-def seed (defaultSeededRef, and
	 * empty) and that nobody has typed into. A duplicate or a draft seeds
	 * the field from prefillRef and never marks it default-seeded, so they
	 * win by construction; a notification or template resolving AFTER this
	 * fill still overwrites it, because the name stays default-seeded. No
	 * record → the field stays blank, exactly as without the option. Never on
	 * an existing item. The API fills nothing — this form is the one place
	 * the option is applied (docs/SCHEMA.md).
	 *
	 * Idempotent rather than latched: a re-run (the card cache refreshing,
	 * StrictMode's double mount) asks the shared useMe cache again and finds
	 * the field already filled, so it cannot fire twice or fight a later
	 * prefill.
	 */
	useEffect(() => {
		if (!isNewRecord || !cardReady || !collectionId) return undefined;
		const wanting = allElementDefs.filter(el => wantsDefaultToMe(el) && el?.name);
		if (!wanting.length) return undefined;
		let active = true;
		wanting.forEach(el => {
			const target = normalizeId(el.options?.lookupCardId);
			if (!target || !lookupFamilyHasIdentity({ cardsById, elementSetsById, cardId: target })) return;
			const source = lookupRecordsCollectionId(cardsById?.[target], collectionId);
			loadMe(source, { cardId: target }).then(async ({ item: mine }) => {
				const meId = normalizeId(mine?._id);
				if (!active || !meId) return;
				// A STRICT picker filter (utils/lookupFilter.js): the person's
				// own record is filled in only if the filter offers it — the
				// save would be refused otherwise, over a value nobody chose.
				if (isStrictLookup(el.options) && !(await isOffered({ collectionId: source, cardId: target, filter: lookupFilterOf(el.options), id: meId }))) return;
				if (!active) return;
				setItem(prev => {
					const current = prev.elements?.[el.name];
					if (userTouchedRef.current.has(el.name)) return prev;
					if (!defaultSeededRef.current.has(el.name) || !isEmptyLookupValue(current)) return prev;
					const next = {
						...prev,
						elements: { ...(prev.elements || {}), [el.name]: withMe(current, meId, { allowMultiple: !!el.options?.allowMultiple }) },
					};
					// A default, not an edit: the form stays pristine if it was.
					markPristineIfAllowed(next);
					return next;
				});
			});
		});
		return () => { active = false; };
	}, [isNewRecord, cardReady, collectionId, allElementDefs, cardsById, elementSetsById, markPristineIfAllowed]);

	// Comments (KPE-005, KPR-307) replace the Notes box. The card's switch is
	// resolved through the hierarchy the same way elements are: the most
	// specific explicit `options.allowComments` wins, and a chain that says
	// nothing is OFF. Until the definition is in hand there is no answer:
	// showing the section on the platform default would flash one onto a card
	// that has them off.
	const commentsAllowed = useMemo(() => (
		cardDefChain.length ? commentsAllowedForChain(cardDefChain) : false
	), [cardDefChain]);

	// Does the section arrive unfolded? The card's own display setting
	// (`options.commentsExpanded`, the rename of `notesExpanded` — KPR-305,
	// absent = collapsed) answers for an item without comments; one that
	// already has some opens regardless — folding away text the person cannot
	// see would be hiding data, not tidying (ItemComments).
	const commentsExpandedDefault = useMemo(
		() => (cardDefChain.length ? commentsExpandedForChain(cardDefChain) : false),
		[cardDefChain]
	);

	// The tag row's changes (`tagIds`, `myTags`, `tagTitles`): part of the
	// form, saved with it. A draft remembers the tags too (T9), so a tag the
	// person picks arms the autosave like typing does.
	const handleTagsChange = useCallback(patch => {
		markDirty();
		draftWorthyRef.current = true;
		setItem(prev => ({ ...prev, ...patch }));
	}, [markDirty]);

	// ⋯ "Tag…" on the form: its own tag row, scrolled to and opened — the
	// dialog the other views open would save apart from the form.
	const openFormTags = useCallback(() => {
		tagRowRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
		setTagPickerOpen(true);
	}, []);

	const handleFEChange = (name, value) => {
		markDirty();
		draftWorthyRef.current = true;
		userTouchedRef.current.add(name);
		defaultSeededRef.current.delete(name);
		// The list a repeating group held a moment ago: which cell an edit changed.
		const previousEntries = entriesOf(item.elements?.[name]);
		setItem(prev => ({ ...prev, elements: { ...prev.elements, [name]: value } }));
		// Clear a field's validation error as soon as it is populated.
		setValidationErrors(prev => {
			const def = allElementDefs.find(d => d?.name === name);
			// A repeating group keeps an error only while the cell it names is
			// still wrong (utils/repeatingGroupEntries reconcileGroupErrors).
			if (isRepeatingGroup(def)) {
				return reconcileGroupErrors(prev, def, entriesOf(value), {
					stored: isNewRecord ? null : (loadedItemRef.current?.elements?.[name] || null),
					attachmentsOff: attachmentsOn === false,
					previous: previousEntries,
				});
			}
			if (!prev[name]) return prev;
			if (def && isElementValueEmpty(def, value)) return prev;
			const next = { ...prev };
			delete next[name];
			return next;
		});
	};

	const savingDisabled = cardLoading || itemLoading || itemError || !item.collection_id || !item.card_id;

	/*
	 * Draft autosave. ONE draft per form session: created on the first save
	 * after the person types, then updated in place (debounced), so a form
	 * that is typed into for ten minutes is one draft, not six hundred. Only
	 * typing arms it (draftWorthyRef) — a duplicate, a notification prefill or
	 * the reseeded "Save & add another" form is dirty without being anything
	 * the person wrote. Saves run one at a time, in order (draftChainRef), and
	 * a GENERATION guards the end of the session: the save that submits or
	 * discards the draft bumps it, and a save still in flight from before
	 * gives up — or, if it had just created a draft, deletes it — rather than
	 * leave an orphan behind the saved item.
	 */
	const sessionDraftIdRef = useRef(null);
	const [sessionDraftId, setSessionDraftId] = useState(null);
	const draftGenRef = useRef(0);
	const draftChainRef = useRef(Promise.resolve());
	const pendingDraftRef = useRef(null);
	const unmountingRef = useRef(false);
	useLayoutEffect(() => {
		unmountingRef.current = false;
		return () => { unmountingRef.current = true; };
	}, []);

	const adoptSessionDraft = useCallback((id) => {
		sessionDraftIdRef.current = id || null;
		setSessionDraftId(id || null);
	}, []);

	// The draft a chip (or `?draft=`) put on the form, as it was when it
	// landed: `{ id, payload, edited }`. "Replace" without saving puts it
	// back the way it was if autosave has since written typing into it —
	// the person chose not to keep that typing, and the draft was theirs
	// before this form touched it.
	const appliedDraftRef = useRef(null);

	// Resolves true when the snapshot is on the server, false when it is not
	// (a failure, or the session ended while it waited).
	const saveDraft = useCallback((snapshot) => {
		const gen = draftGenRef.current;
		const cardId = normalizeId(cardIdParam);
		const run = async () => {
			if (gen !== draftGenRef.current) return false;
			// An entry's client key is the form's own: a draft holds the entries.
			const payload = { elements: withoutEntryKeys(allElementDefs, snapshot.elements || {}) };
			// The form's tags as they stand (T9): the hand tags, and the
			// person's own private tags. The draft only remembers them; opening
			// it asks the API again what lands.
			payload.tagIds = Array.isArray(snapshot.tagIds) ? snapshot.tagIds : [];
			payload.privateTagIds = Array.isArray(snapshot.privateTagIds) ? snapshot.privateTagIds : [];
			try {
				if (sessionDraftIdRef.current) {
					const updating = sessionDraftIdRef.current;
					try {
						await updateDraft(updating, payload);
						if (appliedDraftRef.current?.id === updating) appliedDraftRef.current.edited = true;
						if (gen === draftGenRef.current) setDraftStatus('saved');
						return true;
					} catch (error) {
						// Gone (expired, deleted from the Drafts page in another
						// tab): start a new one rather than lose the typing.
						if (error?.response?.status !== 404) throw error;
						if (gen !== draftGenRef.current) return false;
						adoptSessionDraft(null);
					}
				}
				const created = await createDraft({ collection_id: collectionId, card_id: cardId, ...payload });
				const id = normalizeId(created?._id) || null;
				if (gen !== draftGenRef.current) {
					if (id) deleteDraft(id).catch(() => {});
					return false;
				}
				adoptSessionDraft(id);
				setDraftStatus(id ? 'saved' : 'failed');
				return Boolean(id);
			} catch {
				if (gen === draftGenRef.current) setDraftStatus('failed');
				return false;
			}
		};
		const next = draftChainRef.current.then(run, run);
		draftChainRef.current = next;
		return next;
	}, [collectionId, cardIdParam, adoptSessionDraft, allElementDefs]);

	/** Saves whatever is waiting on the debounce now. */
	const flushDraft = useCallback(() => {
		const pending = pendingDraftRef.current;
		pendingDraftRef.current = null;
		return pending ? saveDraft(pending) : draftChainRef.current;
	}, [saveDraft]);

	/**
	 * Ends the session's draft: the item it became was saved, or the person
	 * discarded it. Nothing in flight may write it again, and the row goes.
	 */
	const closeSessionDraft = useCallback(() => {
		draftGenRef.current += 1;
		pendingDraftRef.current = null;
		draftWorthyRef.current = false;
		appliedDraftRef.current = null;
		const id = sessionDraftIdRef.current;
		adoptSessionDraft(null);
		setDraftStatus('idle');
		if (id) deleteDraft(id).catch(() => { /* it expires in 30 days regardless */ });
	}, [adoptSessionDraft]);

	/**
	 * "Replace" without saving: what the form holds is not kept. A draft
	 * this session made of it is deleted; a draft the form was applied from
	 * stays in the list as it was when it landed — put back so, if autosave
	 * has written typing into it since. Queued behind any save in flight,
	 * which then gives up (the generation moves on).
	 */
	const dropFormDraft = useCallback(() => {
		const typed = draftWorthyRef.current;
		draftGenRef.current += 1;
		pendingDraftRef.current = null;
		draftWorthyRef.current = false;
		const id = sessionDraftIdRef.current;
		const applied = appliedDraftRef.current;
		appliedDraftRef.current = null;
		adoptSessionDraft(null);
		setDraftStatus('idle');
		if (!id) return;
		let settle = null;
		if (applied?.id !== id) settle = () => deleteDraft(id);
		else if (applied.edited || typed) settle = () => updateDraft(id, applied.payload);
		if (!settle) return;
		draftChainRef.current = draftChainRef.current.then(() => settle().catch(() => { /* it expires in 30 days regardless */ }));
	}, [adoptSessionDraft]);

	// What the form holds right now, as a draft would keep it.
	const formSnapshotRef = useRef(null);
	const snapshotOf = current => ({
		elements: current.elements || {},
		tagIds: handTagIdsOf(current),
		privateTagIds: ownPrivateTagIdsOf(current),
	});
	formSnapshotRef.current = snapshotOf(item);

	/** "Save mine as a draft": what the form holds is saved before anything replaces it. */
	const keepFormDraft = useCallback(async () => {
		pendingDraftRef.current = null;
		const saved = await saveDraft(formSnapshotRef.current);
		// Whatever it was applied from now holds this: nothing to put back.
		if (saved) appliedDraftRef.current = null;
		return saved;
	}, [saveDraft]);

	useEffect(() => {
		pendingDraftRef.current = null;
		if (!draftsEnabled || !cardReady || !isDirty || !draftWorthyRef.current) return undefined;
		const snapshot = snapshotOf(item);
		pendingDraftRef.current = snapshot;
		const timer = setTimeout(() => {
			if (pendingDraftRef.current !== snapshot) return;
			pendingDraftRef.current = null;
			saveDraft(snapshot);
		}, DRAFT_DEBOUNCE_MS);
		return () => {
			clearTimeout(timer);
			// Leaving the page mid-debounce (a link, the rail, the back
			// button) keeps the last second of typing too.
			if (unmountingRef.current && pendingDraftRef.current === snapshot) {
				pendingDraftRef.current = null;
				saveDraft(snapshot);
			}
		};
	}, [draftsEnabled, cardReady, isDirty, item.elements, item.tagIds, item.myTags, saveDraft]); // eslint-disable-line react-hooks/exhaustive-deps

	// This person's drafts of this card here, for the chips. Fetched per form
	// — never cached — so an account switch in the same tab cannot show the
	// previous account's.
	const [availableDrafts, setAvailableDrafts] = useState([]);
	const [draftsRefreshKey, setDraftsRefreshKey] = useState(0);
	useEffect(() => {
		if (!draftsEnabled) return undefined;
		let active = true;
		(async () => {
			try {
				const result = await listDrafts({ collectionId, cardId: normalizeId(cardIdParam), limit: 20 });
				const rows = Array.isArray(result?.drafts) ? result.drafts : [];
				if (active) setAvailableDrafts(rows.filter(row => row && row.available !== false));
			} catch {
				if (active) setAvailableDrafts([]);
			}
		})();
		return () => { active = false; };
	}, [draftsEnabled, collectionId, cardIdParam, draftsRefreshKey]);
	const chipDrafts = useMemo(
		() => availableDrafts.filter(row => normalizeId(row?._id) !== sessionDraftId),
		[availableDrafts, sessionDraftId]
	);

	/**
	 * The card's new-item form, empty: every element at its default. A draft
	 * lands on this, never on what the form held — applying REPLACES, it
	 * never merges. A merge kept whatever the draft did not carry — what was
	 * typed about resident A survived "Replace" with resident B's draft,
	 * autosaved into B's draft and saved onto B's record.
	 */
	const newItemDefaults = useCallback(() => {
		const blank = {};
		allElementDefs.forEach(el => {
			if (el?.name) blank[el.name] = getElementInitialValue(el, { isNewRecord: true, zone });
		});
		return blank;
	}, [allElementDefs, zone]);

	/** A draft as the form will hold it once applied. */
	const incomingFromDraft = useCallback((draft) => {
		const elements = draft?.elements && typeof draft.elements === 'object' ? draft.elements : {};
		return {
			elements: { ...newItemDefaults(), ...elements },
		};
	}, [newItemDefaults]);

	// A draft of another collection or card (a stale `?draft=` link, a
	// hand-edited URL) is refused and NOT adopted: adopting it would let
	// saving this item delete that one.
	const draftBelongsHere = useCallback(draft => (
		normalizeId(draft?.collection_id) === normalizeId(collectionId)
		&& normalizeId(draft?.card_id) === normalizeId(cardIdParam)
	), [collectionId, cardIdParam]);

	const [applyingDraftId, setApplyingDraftId] = useState(null);

	/**
	 * Fills the form from one of this person's drafts and makes it the
	 * session's draft, so what they type next updates it and saving the item
	 * deletes it. What the form held has already been answered for by the
	 * caller: kept as a draft, or dropped.
	 */
	const fillFromDraft = useCallback((draft, draftId) => {
		const id = normalizeId(draftId || draft?._id);
		const incoming = incomingFromDraft(draft);
		const elements = draft?.elements && typeof draft.elements === 'object' ? draft.elements : {};
		const has = name => Object.prototype.hasOwnProperty.call(elements, name);
		userTouchedRef.current = new Set(Object.keys(elements));
		defaultSeededRef.current = new Set(Object.keys(incoming.elements).filter(name => !has(name)));
		// A draft replaces the form: no prefill is on it any more.
		prefilledRef.current = new Set();
		// If the definition seeds AFTER this (a `?draft=` arrival), it
		// seeds these values instead of the card defaults.
		prefillRef.current = { ...elements };
		prefillIsDuplicateRef.current = false;
		// The draft's files (F2 keep, PR 10b): its own held uploads show
		// through the held route; an id a Duplicate seeded, through its source
		// item when the facts name it. The draft keeps them — never this form.
		seedHeldFiles(draft?.files, { heldIn: normalizeId(collectionId) });
		markDirty();
		setItem(prev => ({
			...prev,
			elements: incoming.elements,
			// A draft replaces the form's tags too (T9): none until the API
			// says which of the draft's still land (below) — but the ACTIVE
			// TAG, which every new form gets, is back on at once: a draft is
			// touched by it when it is saved (b48). ⚑ It goes back on even
			// if the person had left it off before the draft replaced the form.
			tagIds: [],
			myTags: activeTagOnFormRef.current ? [{ tagId: activeTagOnFormRef.current, kind: 'tag' }] : [],
		}));
		setLaunchTagNote(null);
		const draftTagIds = [...(Array.isArray(draft?.tagIds) ? draft.tagIds : []), ...(Array.isArray(draft?.privateTagIds) ? draft.privateTagIds : [])];
		if (draftTagIds.length) {
			getUsableTags(collectionId, draftTagIds)
				.then(launch => applyLaunchTags(launch, 'the draft'))
				.catch(() => setLaunchTagNote('The draft’s tags could not be checked, so none were put on. Add them from the Tags row.'));
		}
		setValidationErrors({});
		draftWorthyRef.current = false;
		appliedDraftRef.current = {
			id,
			payload: {
				elements,
				tagIds: Array.isArray(draft?.tagIds) ? draft.tagIds : [],
				privateTagIds: Array.isArray(draft?.privateTagIds) ? draft.privateTagIds : [],
			},
			edited: false,
		};
		adoptSessionDraft(id);
		setDraftStatus('idle');
		setDraftsRefreshKey(key => key + 1);
	}, [incomingFromDraft, markDirty, adoptSessionDraft, collectionId, applyLaunchTags, seedHeldFiles]);

	/** One draft with its values, or null (gone, or not this card's). */
	const loadDraftForForm = useCallback(async (draftId) => {
		const draft = await getDraft(normalizeId(draftId));
		return draft && draftBelongsHere(draft) ? draft : null;
	}, [draftBelongsHere]);

	// `?draft=<id>`: the Drafts page's Open, onto a blank form. Anything
	// typed before it arrived is flushed to its own draft first.
	const applyDraftNow = useCallback(async (draftId) => {
		const id = normalizeId(draftId);
		if (!id) return;
		setApplyingDraftId(id);
		try {
			let draft;
			try {
				draft = await getDraft(id);
			} catch {
				message.warning('That draft is no longer available.');
				setDraftsRefreshKey(key => key + 1);
				return;
			}
			if (!draft) return;
			if (!draftBelongsHere(draft)) {
				message.warning('This draft belongs to another card.');
				return;
			}
			await flushDraft();
			// What the form uploaded is in the draft just flushed (or in none,
			// and goes).
			settleHeldFiles(sessionDraftIdRef.current ? fileIdsIn(formSnapshotRef.current?.elements) : []);
			fillFromDraft(draft, id);
		} finally {
			setApplyingDraftId(null);
		}
	}, [flushDraft, fillFromDraft, draftBelongsHere, message, settleHeldFiles, fileIdsIn]);

	/*
	 * A chip's Apply. When applying would replace or clear something the
	 * form holds (draftDiff.losesSomething), "Replace what is in the form?"
	 * shows what changes, field by field, and asks: Replace drops it, "Save
	 * mine as a draft" keeps it in the drafts first. When nothing would be
	 * lost — the form is untouched (only its own defaults, a "me" lookup
	 * among them), every value it holds is the draft's too, or it holds an
	 * applied draft exactly as it landed (still in the list, untouched —
	 * nothing typed, no switch moved) — the draft goes straight on.
	 */
	const [replaceAsk, setReplaceAsk] = useState(null);
	const [replaceBusy, setReplaceBusy] = useState(null);
	const requestApplyDraft = useCallback(async (draftId, prefetched = null) => {
		const id = normalizeId(draftId);
		if (!id) return;
		setApplyingDraftId(id);
		try {
			let draft = prefetched;
			if (!draft) {
				try {
					draft = await getDraft(id);
				} catch {
					message.warning('That draft is no longer available.');
					setDraftsRefreshKey(key => key + 1);
					return;
				}
			}
			if (!draft) return;
			if (!draftBelongsHere(draft)) {
				message.warning('This draft belongs to another card.');
				return;
			}
			// Values the form put there itself, untouched since — a card
			// default, a "default to me" lookup. Not a prefill: a notification
			// CTA's or a quick add's values are somebody's, and replacing them
			// is a loss (prefilledRef).
			const seeded = new Set([...defaultSeededRef.current]
				.filter(name => !userTouchedRef.current.has(name) && !prefilledRef.current.has(name)));
			const rows = draftReplaceRows({
				elementDefs: allElementDefs,
				defaults: newItemDefaults(),
				current: formSnapshotRef.current,
				incoming: incomingFromDraft(draft),
				seeded,
			});
			// An applied draft is still in the list as it landed, so leaving it
			// loses nothing — as long as the form still holds exactly that.
			// Asked of the FORM, not of "has anyone typed": what the form holds is
			// compared with the draft as it landed (the review of the live-pass
			// merges).
			const sessionId = sessionDraftIdRef.current;
			const applied = appliedDraftRef.current;
			const untouchedDraft = Boolean(sessionId)
				&& applied?.id === sessionId
				&& !draftWorthyRef.current
				&& !draftReplaceRows({
					elementDefs: allElementDefs,
					defaults: newItemDefaults(),
					current: formSnapshotRef.current,
					incoming: incomingFromDraft(applied.payload),
					seeded,
				}).length;
			if (!isDirty || !losesSomething(rows) || untouchedDraft) {
				dropFormDraft();
				settleHeldFiles([]);
				fillFromDraft(draft, id);
				return;
			}
			setReplaceAsk({ id, draft, rows });
		} finally {
			setApplyingDraftId(null);
		}
	}, [message, draftBelongsHere, allElementDefs, newItemDefaults, incomingFromDraft, isDirty, dropFormDraft, fillFromDraft, settleHeldFiles]);

	const answerReplace = useCallback(async (keepMine) => {
		const ask = replaceAsk;
		if (!ask || replaceBusy) return;
		if (keepMine) {
			setReplaceBusy('keep');
			const saved = await keepFormDraft();
			setReplaceBusy(null);
			if (!saved) {
				message.error('Your draft could not be saved, so nothing was replaced. Please try again.');
				return;
			}
			// "Save mine as a draft": the form's uploads went into it.
			settleHeldFiles(fileIdsIn(formSnapshotRef.current?.elements));
		} else {
			dropFormDraft();
			settleHeldFiles([]);
		}
		setReplaceAsk(null);
		fillFromDraft(ask.draft, ask.id);
	}, [replaceAsk, replaceBusy, keepFormDraft, dropFormDraft, fillFromDraft, message, settleHeldFiles, fileIdsIn]);

	/** A chip's Delete, confirmed in its popover. */
	const deleteChipDraft = useCallback(async (draftId) => {
		const id = normalizeId(draftId);
		try {
			await deleteDraft(id);
		} catch {
			message.error('Deleting that draft failed. Please try again.');
			return;
		}
		setAvailableDrafts(rows => rows.filter(row => normalizeId(row?._id) !== id));
		message.success('Draft deleted.');
	}, [message]);

	// `?draft=<id>`: the Drafts page's Open. Applied once, onto a blank form.
	const draftParamDoneRef = useRef(false);
	useEffect(() => {
		if (!draftParamId || draftParamDoneRef.current) return;
		draftParamDoneRef.current = true;
		applyDraftNow(draftParamId);
	}, [draftParamId, applyDraftNow]);

	/**
	 * "Save & add another": reset for rapid repeat capture. Dates re-stamp
	 * (their defaults re-run — falling back to the previous value so a
	 * hand-picked date survives), lookups (the "person") carry over, and
	 * everything else returns to its card default.
	 */
	const resetForRepeat = useCallback(() => {
		setItem(prev => {
			const nextElements = {};
			allElementDefs.forEach(def => {
				const name = def?.name;
				if (!name) return;
				const prior = prev.elements?.[name];
				if (def.dataType === 'date' || def.dataType === 'date-time') {
					const fresh = getElementInitialValue(def, { isNewRecord: true, zone });
					// "No default" is the EMPTY seed in every shape it comes in:
					// an element that holds several dates seeds as [], and an
					// empty list is no more a re-stamp than a null is — without
					// this the hand-picked dates that survive on a single-date
					// field would be wiped on a list.
					const seeded = !(fresh === undefined || fresh === null || fresh === ''
						|| (Array.isArray(fresh) && !fresh.length));
					nextElements[name] = seeded ? fresh : prior;
				} else if (def.dataType === 'card-lookup') {
					nextElements[name] = prior;
				} else {
					// A measurement clears its magnitude here; the unit it
					// reopens in is the one last used for the element (the
					// control remembers it per browser), so a run of entries
					// in kg stays in kg without a value to carry it.
					nextElements[name] = getElementInitialValue(def, { isNewRecord: true, zone });
				}
			});
			return { ...prev, elements: nextElements };
		});
		setRepeatEpoch(count => count + 1);
		setValidationErrors({});
		setPanelOpen({});
		userTouchedRef.current = new Set();
		// The reseeded form is a CHANGE, same as a prefill: Save must work
		// without the user having to touch a field.
		markDirty();

		// A quick add re-resolves on repeat so `{{last.x}}` sees the item
		// just saved: statics and formulas both land on top of the reset —
		// only typing that happens while the resolve is in flight survives.
		// A duplicate profile re-resolves against the SAME source: another
		// copy of it, taken fresh where the profile says so.
		if (templateId && collectionId) {
			resolveLaunch()
				.then(resolved => {
					const elements = resolved?.elements && typeof resolved.elements === 'object' ? resolved.elements : {};
					reportTemplateWarnings(resolved?.warnings);
					setItem(prev => {
						const nextElements = { ...(prev.elements || {}) };
						Object.entries(elements).forEach(([name, value]) => {
							if (!userTouchedRef.current.has(name)) nextElements[name] = value;
						});
						return { ...prev, elements: nextElements };
					});
				})
				.catch(reportTemplateFailure);
		}
	}, [allElementDefs, markDirty, templateId, collectionId, reportTemplateWarnings, reportTemplateFailure, resolveLaunch, zone]);

	// One save at a time. Save stays enabled while the POST is in flight; a
	// second click or a ⌘↵ in that window would create the item twice.
	const submittingRef = useRef(false);
	const handleSubmit = (addAnother = false) => {
		if (!isDirty || submittingRef.current) return;
		// A locked record offers no Save; ⌘↵ does not find one either.
		if (recordClosed) return;

		// Never validate (or save) against a definition that failed to load —
		// an empty definition would let required fields through unchecked.
		if (!cardDef) {
			message.error('The card definition is still loading. Please try again in a moment.');
			return;
		}

		// Missing required values AND values the API would refuse, in
		// definition order (utils/elements getElementValidationErrors), so the
		// first key is the topmost field on the form.
		// `stored` is the echo rule's input: a link or a paragraph the item
		// already holds from before a rule is never refused here, as the API
		// never refuses it (utils/elementValidation isEcho).
		const errors = getElementValidationErrors(allElementDefs, item.elements, {
			stored: isNewRecord ? null : (loadedItemRef.current?.elements || null),
			// A file element is not asked for while the collection takes no files.
			attachmentsOff: attachmentsOn === false,
		});
		// A repeating group is judged entry by entry (its minimum, each entry's
		// columns, a heading's title), the messages filed under the group and
		// the cell they are about (utils/repeatingGroupEntries).
		allElementDefs.filter(isRepeatingGroup).forEach(def => {
			Object.assign(errors, validateGroupEntries(def, entriesOf(item.elements?.[def.name]), {
				stored: isNewRecord ? null : (loadedItemRef.current?.elements?.[def.name] || null),
				attachmentsOff: attachmentsOn === false,
			}));
		});
		// The topmost field ON SCREEN: a form layout may put a later element
		// first, so the rendered order decides, not the definition order.
		const firstError = renderedOrderRef.current.find(name => errors[name]) || Object.keys(errors)[0];
		if (firstError) {
			setValidationErrors(errors);
			// Save stays enabled; the field itself names what is wrong. Every
			// group holding a field to fix opens (again, if its author closed
			// it since the last attempt), and the scroll waits for that to
			// commit.
			revealFields(Object.keys(errors));
			scrollToField(firstError);
			return;
		}
		setValidationErrors({});
		lastAddAnotherRef.current = addAnother;
		setSaveFailed(false);
		setPlanLimit(null);

		// The repeating groups of this form, and how each is written on an
		// update: not at all (unchanged), as single-entry operations, or whole.
		// Set by wirePayload for the source it builds the body from.
		const groupDefsOnForm = allElementDefs.filter(isRepeatingGroup);
		let groupPlans = {};

		// The body for `source`: the form as it stands, or — after a stale
		// save — the form with the other person's values merged in.
		const wirePayload = (source) => {
			groupPlans = isNewRecord ? {} : planGroupWrites(groupDefsOnForm, source?.elements || {}, loadedItemRef.current?.elements || {});
			const payload = withoutLegacyTags(source);
			// The item read's file facts (PR 10b) are the server's to say,
			// never sent back.
			delete payload.files;

			// Every element the card DECLARES goes on the wire, spelled out.
			// `undefined` is not a value JSON can carry — JSON.stringify drops the
			// key — so a field cleared by an input that reported `undefined` used
			// to reach the API as SILENCE, and the write backfilled '' for it. That
			// worked only by accident, and only for as long as an omitted key means
			// "blank this": say what is meant instead of relying on the server to
			// guess. One loop at the wire edge, so no input component can reopen
			// the hole.
			payload.elements = { ...(payload.elements || {}) };
			allElementDefs.forEach(def => {
				const name = def?.name;
				if (!name) return;
				const held = Object.prototype.hasOwnProperty.call(payload.elements, name)
					? payload.elements[name]
					: undefined;
				if (held !== undefined) return;
				// The element's own EMPTY spelling — '' for text, null for a date
				// or a measurement, [] for a list — never a card default: this is
				// a field with nothing in it, not a fresh form.
				payload.elements[name] = getElementInitialValue(def, { isNewRecord: false });
			});
			// A repeating group (KPR-680) goes as the API takes it: each entry's
			// changeable columns, its id kept on an update, `_key` and a copied
			// entry's id never. On an update, a group whose changes go as
			// single-entry operations — or has none — is LEFT OUT of this write
			// (an omitted key keeps what is stored), so a colleague's entries
			// are never put back. One a person may only see is never sent.
			groupDefsOnForm.forEach(def => {
				if (isSeeOnlyElement(def)) {
					if (isNewRecord) payload.elements[def.name] = '';
					else delete payload.elements[def.name];
					return;
				}
				if (isNewRecord) {
					payload.elements[def.name] = entriesForWire(def, entriesOf(source.elements?.[def.name]), { isNew: true });
					return;
				}
				const mode = groupPlans[def.name]?.mode || 'none';
				if (mode === 'whole') payload.elements[def.name] = entriesForWire(def, entriesOf(source.elements?.[def.name]));
				else delete payload.elements[def.name];
			});
			// …except a "Me too" count (KPR-390), which is never on the wire,
			// a create's or an edit's: keepr counts it from the people who
			// confirm, and somebody may have confirmed since this form read it
			// (a confirmation moves no updatedAt). An omitted key keeps what is
			// stored (utils/confirmationValues.js withoutConfirmations).
			payload.elements = withoutConfirmations(payload.elements, allElementDefs);
			// A NEW item never states a value for an element a rule holds this
			// person to "see only" (KPR-509): a Duplicate, a quick add, a
			// prefill or a draft may have put one there, the field is disabled so
			// they could not take it out, and the API refuses a create that sends
			// anything but blank or the default (`element_read_only`, reason
			// `grant`). Blank reads as not sent, so the card's default applies.
			if (isNewRecord) {
				allElementDefs.forEach(def => {
					if (def?.name && isSeeOnlyElement(def)) payload.elements[def.name] = getElementInitialValue(def, { isNewRecord: false });
				});
			}

			// An item's `notes` (the section comments replaced) is never written
			// from here: a save leaves what is stored untouched (an omitted key
			// is "no change" to the API), and a copy or a draft mints none.
			delete payload.notes;

			// TAGS (docs/plans/tags.md §5.2). The hand-applied list rides the
			// write — on a new item when there is one, on an update only when
			// the form changed it (an unchanged list is an echo the API would
			// save anyway; leaving it out keeps the write about what changed).
			// Never `tagAutoIds` (a rule's), `myTags` or `tagTitles`.
			TAG_KEYS_NEVER_SENT.forEach(key => { delete payload[key]; });
			const tagList = handTagIdsOf(source);
			delete payload.tagIds;
			if (isNewRecord) {
				if (tagList.length) payload.tagIds = tagList;
			} else if (!sameTagIds(tagList, loadedItemRef.current?.tagIds || [])) {
				payload.tagIds = tagList;
			}
			return payload;
		};

		// PRIVATE TAGS go on once the item exists (a new one has no id until
		// its create lands): the form's `myTags` against what it read, through
		// the person's own store. The item is saved either way; a refusal here
		// is said, and the form's save is not undone for it.
		const privateTagsToSend = privateDelta(isNewRecord ? [] : loadedMyTagsRef.current, item.myTags);
		const applyPrivateAfterSave = async (savedId) => {
			const id = normalizeId(savedId);
			if (!id || (!privateTagsToSend.add.length && !privateTagsToSend.remove.length)) return;
			const rows = list => list.map(entry => ({ tagId: entry.tagId, targetType: 'item', target_id: id }));
			try {
				if (privateTagsToSend.add.length) await applyPrivateTags(rows(privateTagsToSend.add));
				if (privateTagsToSend.remove.length) await removePrivateTags(rows(privateTagsToSend.remove));
				if (!isNewRecord) loadedMyTagsRef.current = item.myTags || [];
			} catch (error) {
				message.warning(`The item was saved, but its private tags were not: ${privateTagRefusal(error)}`);
			}
		};
		const sayDroppedTags = data => {
			const note = tagDroppedNote(data?.warnings);
			if (note) message.warning(note);
		};
		// What the save's CALCULATIONS found (KPR-828): a value keepr could not work
		// out is stored blank and rides the answer as a `formula_warning`, from the
		// write itself or from any entry door. They are collected over the whole
		// save and said once, with the group, the entry and the column named.
		const formulaBatches = [];
		const formulaFinal = { elements: null };
		const takeFormulaWarnings = (answer) => {
			formulaBatches.push(formulaWarningsOf(answer));
			if (answer?.elements && typeof answer.elements === 'object') formulaFinal.elements = answer.elements;
		};
		const sayFormulaWarnings = () => {
			const note = formulaWarningsNotice(
				mergeFormulaWarnings(formulaBatches, formulaFinal.elements),
				{ defs: allElementDefs, elements: item.elements || {} }
			);
			if (note) message.warning({ content: note, duration: 10 });
		};
		const payload = wirePayload(item);

		// A refusal the API answered about ONE field goes back to that field.
		// 400 `range` is the date bounds' own code — the web enforces them
		// exactly in local time and the API with a day of slack in UTC, so a
		// refusal that gets here is a genuine disagreement about an edge, and
		// it must be readable where it can be fixed.
		// The field may sit in a collapsed group: it opens, and the scroll
		// waits for the commit that opens it — as the client-side path does.
		// Scrolling here, synchronously, aimed at a field still inside a
		// `hidden` container and went nowhere (review finding).
		const landOnField = (elementName, text, toast) => {
			setValidationErrors(prev => ({ ...prev, [elementName]: text }));
			revealFields([elementName]);
			scrollToField(elementName);
			message.error(`The item was not saved: ${toast}`);
		};
		// A refusal about ONE ENTRY of a repeating group goes to that entry —
		// and, when it names a column, to that column: `element` is the group,
		// beside `position` / `entryId` (the list as it was sent) and `column`.
		const landOnEntry = (def, key, column, text) => {
			setValidationErrors(prev => ({
				...prev,
				[def.name]: prev[def.name] || 'An entry needs attention.',
				[entryErrorKey(def.name, key, column)]: text,
			}));
			revealFields([def.name]);
			scrollToField(def.name);
			message.error(`The item was not saved: ${text}`);
		};
		// A save refused for the cost of its calculations (400 `formula_too_costly`,
		// `element?` / `column?` — the group or calculated element it names, when this
		// person can read it): the server's own sentence, on the group it names (or the
		// group the entry change was about), and as the toast. Nothing was written and
		// nothing is reset: every value stays in the form.
		const landOnCost = (error, fallbackGroup = null) => {
			const text = apiErrorMessage(error);
			const named = error?.response?.data?.element;
			const target = (named && groupDefsOnForm.find(def => def.name === named)) || fallbackGroup;
			if (target) {
				setValidationErrors(prev => ({ ...prev, [target.name]: text }));
				revealFields([target.name]);
				scrollToField(target.name);
			}
			message.error(text);
		};
		const handleWriteError = (error) => {
			const data = error?.response?.data;
			// A plan limit: nothing was written and every value stays in the
			// form. Said in the PlanLimit pattern above the form, and as the toast.
			const limitRefusal = planLimitOf(error);
			if (limitRefusal) {
				setPlanLimit(limitRefusal);
				message.error(`The item was not saved: ${planLimitWords(limitRefusal).title}`);
				return;
			}
			if (isFormulaTooCostly(error)) {
				landOnCost(error);
				return;
			}
			// The record is locked (keepr-api cardRecordPolicy, 409
			// `item_locked`): said in the API's words, the form switches to its
			// locked state with Unlock and Correct, and every value stays. No
			// Retry: it would only be refused again.
			if (isLockedError(error)) {
				const reason = apiErrorMessage(error) || 'This record is locked.';
				setLockRefusal(reason);
				setSaveFailed(false);
				message.error(`The item was not saved: ${reason}`);
				return;
			}
			const refusedGroup = data?.element ? groupDefsOnForm.find(def => def.name === data.element) : null;
			if (refusedGroup && error?.response?.status >= 400 && error.response.status < 500) {
				const text = plainRefusal(apiErrorMessage(error));
				if (data.code === 'too_many_entries' || data.code === 'too_few_entries') {
					landOnField(refusedGroup.name, text || apiErrorMessage(error), text || apiErrorMessage(error));
					return;
				}
				const key = entryKeyFromRefusal(item.elements?.[refusedGroup.name], data);
				if (key && text) {
					const column = data.column && visibleColumns(refusedGroup).some(col => col.name === data.column) ? data.column : ENTRY_COLUMN;
					landOnEntry(refusedGroup, key, column, text);
					return;
				}
			}
			if (error?.response?.status === 400 && (data?.code === 'range' || (data?.code === 'element_read_only' && data.reason === 'grant')) && data.element) {
				const text = data.code === 'range' ? describeRangeRefusal(data, { elements: allElementDefs }) : apiErrorMessage(error);
				landOnField(data.element, text, text);
				return;
			}
			// The data types second pass's refusals (docs/plans/
			// data-types-second-pass.md PR 1): a value past its element's
			// ceiling (`too_long`, with the `limit` it was held to) and a link
			// the one URL rule refuses (`invalid_url`). The form checks both
			// before a save, so one reaching here is a value it could not see
			// (a stale card) — it still lands on the field, in the form's words.
			if (error?.response?.status === 400 && (data?.code === 'too_long' || data?.code === 'invalid_url') && data.element) {
				const def = allElementDefs.find(entry => entry?.name === data.element);
				const label = def?.label?.singular || data.element;
				const text = data.code === 'too_long'
					? `${label} is limited to ${formatLimit(Number(data.limit) || 0)} characters.`
					: (validateElementValue(def || { dataType: 'url', name: data.element }, item.elements?.[data.element]) || `${label} is not a link keepr can store.`);
				landOnField(data.element, text, text);
				return;
			}
			// A short text that does not fit its element's pattern (PR 2, 400
			// `pattern`): the author's sentence, from the card the form holds
			// — the API's message carries it too, after "elements.<name>: ".
			if (error?.response?.status === 400 && data?.code === 'pattern' && data.element) {
				const def = allElementDefs.find(entry => entry?.name === data.element);
				const fromApi = typeof data.message === 'string' ? data.message.replace(/^elements\.[^:]+:\s*/, '') : '';
				const text = patternMessageFor(def) || fromApi || `${def?.label?.singular || data.element} does not have the expected format.`;
				landOnField(data.element, text, text);
				return;
			}
			// A color the one color rule cannot read (PR 9, 400
			// `invalid_color`): the form checks it first, so one reaching here
			// is a value it could not see — it lands on the field all the same.
			if (error?.response?.status === 400 && data?.code === 'invalid_color' && data.element) {
				const def = allElementDefs.find(entry => entry?.name === data.element);
				const text = validateElementValue(def || { dataType: 'color', name: data.element }, item.elements?.[data.element])
					|| `${def?.label?.singular || data.element} is not a color keepr can store.`;
				landOnField(data.element, text, text);
				return;
			}
			// Unique values (keepr-api utils/uniqueValues.js): another item of
			// the card already has this value. The server names that item only
			// when this person can read it — then the field says "Already used
			// by" with its badge (a door to it); otherwise it says another item
			// has the value, and nothing more.
			if (isDuplicateValue(error) && data.element) {
				const { text, toast, holder } = describeDuplicateValue(data, { elements: allElementDefs });
				landOnField(data.element, holder ? (
					<span className='item-form__duplicate'>
						{text}{' '}
						<RecordBadge
							kind='item'
							record={cardDef || {}}
							title={holder.title || 'Untitled'}
							size='xs'
							to={`/collections/${collectionId}/items/${holder.itemId}`}
						/>
					</span>
				) : text, toast);
				return;
			}
			// A file element's refusals (PR 10b, the 10b contract's codes): a
			// file that is gone or someone else's, one used twice, past 20, too
			// large, not a photo, or attachments turned off — in the server's
			// words, at the element.
			if (FILE_REFUSAL_CODES.includes(data?.code) && data.element) {
				const def = allElementDefs.find(entry => entry?.name === data.element);
				const text = serverFileRefusal(error, def || null);
				landOnField(data.element, text, text);
				return;
			}
			// A STRICT lookup's filter does not offer the record (PR 6, 400
			// `lookup_filtered_out`; utils/lookupFilter.js). The picker offers
			// only those, so this is a record that left the filter since it
			// was picked: the field says so and asks for another.
			if (error?.response?.status === 400 && data?.code === 'lookup_filtered_out' && data.element) {
				const text = describeFilteredOut(data, { elements: allElementDefs });
				landOnField(data.element, text, text);
				return;
			}
			// A user element naming an account that no longer exists (or was
			// never one) — the picker only offers real accounts, so this is a
			// stale value or a deactivated person: pick again.
			if (error?.response?.status === 400 && data?.code === 'user_not_found' && data.element) {
				const text = 'That account no longer exists or is inactive. Pick someone else.';
				landOnField(data.element, text, text);
				return;
			}
			// An item lookup naming an item this person may not link (keepr-api
			// § Lookup values on item writes): deleted, or one they cannot see —
			// an item someone else made that this person cannot read, carried in by
			// a duplicate or a quick add. The API answers both alike, and so do
			// these words.
			if (error?.response?.status === 400 && data?.code === 'lookup_not_found' && data.element) {
				const text = 'That item can\'t be linked here. It may have been deleted, or it isn\'t one you can see. Pick another or clear the field.';
				landOnField(data.element, text, text);
				return;
			}
			// The card's account link already names this person on another
			// item of the collection (one person, one record): the message
			// lands on the link element and opens the record that holds it.
			if (error?.response?.status === 409 && data?.code === 'account_already_linked') {
				const link = identityElementOf(allElementDefs);
				const holderId = normalizeId(data.itemId);
				const toast = 'another item in this collection is already linked to that account.';
				if (link) {
					landOnField(link.name, (
						<span>
							Another item in this collection is already linked to that account
							{holderId ? <>: <Link to={`/collections/${collectionId}/items/${holderId}`}>open it</Link></> : null}.
						</span>
					), toast);
					return;
				}
				message.error(`The item was not saved: ${toast}`);
				return;
			}
			// On a card a group built from records reads, linking an account
			// adds that person to the group, so it needs the group's access
			// across the whole collection (keepr-api § Dynamic groups, 403
			// `account_link_needs_access`): the refusal lands on the link.
			// When the link stays and the save changes an element the group's
			// rule reads (owner ruling b78, `changed`) — or one a rule reads
			// THROUGH this record (b79, `changed: 'related'`) — it lands on
			// THAT element; tags, a record leaving or the save itself
			// have no field to land on. (The notice above each locked field
			// usually says so before Save.)
			if (error?.response?.status === 403 && data?.code === 'account_link_needs_access') {
				const link = identityElementOf(allElementDefs);
				const text = apiErrorMessage(error);
				const target = data.changed ? data.element : link?.name;
				if (target) {
					landOnField(target, text, text);
					return;
				}
				message.error(`The item was not saved: ${text}`);
				return;
			}
			// A card a linked collection lends this one is read here, never
			// added to (keepr-api § Merged lists, 403 `card_borrowed`): its
			// records are created in their own collection.
			if (error?.response?.status === 403 && data?.code === 'card_borrowed') {
				const from = data?.borrowedFrom?.collection?.name || data?.borrowedFrom?.name;
				message.error(from
					? `The item was not saved: this card belongs to ${from}. Its records are added there, not here.`
					: `The item was not saved: ${describeLinkError(error)}`);
				return;
			}
			// A refusal about the tags (a restricted tag, one a rule owns, the
			// cap) lands on the tag row, in the API's reason made plain.
			if (isTagRefusal(error)) {
				tagRowRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
				message.error(`The item was not saved: ${tagWriteRefusal(error)}`);
				return;
			}
			message.error('Something went wrong. The item may not have saved.');
		};

		// A refused single-entry operation, said on the entry it was about (the
		// door answers 404 `unknown_entry` for one that is gone, 400 with the
		// `column` for a value, and the limits as `too_many_entries` /
		// `too_few_entries` on the group). Anything else is the form's usual
		// refusal.
		const placeEntryRefusal = (def, op, error) => {
			const data = error?.response?.data;
			const status = error?.response?.status;
			const text = plainRefusal(apiErrorMessage(error));
			if (status === 404 && data?.code === 'unknown_entry') {
				landOnEntry(def, op.key, ENTRY_COLUMN, 'Someone removed this entry since you opened the item. Remove it here too, or add it again.');
				return;
			}
			// The door read the item again five times and somebody saved each time.
			if (isStaleItem(error)) {
				message.warning('Somebody changed this item again while it was saving, so some of your entry changes did not land. The form still holds them — save again to try once more.');
				return;
			}
			if (isFormulaTooCostly(error)) {
				landOnCost(error, def);
				return;
			}
			if (status >= 400 && status < 500 && (data?.code === 'too_many_entries' || data?.code === 'too_few_entries') && text) {
				landOnField(def.name, text, text);
				return;
			}
			// A removal the API refused (attachments are off, and the entry holds a
			// file that would go with it - 400 `attachments_disabled`, KPR-750): the
			// entry is already off the form, so the sentence stands on the group,
			// where "Undo" is.
			if (op.op === 'remove' && status >= 400 && status < 500 && text) {
				const sentence = `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
				landOnField(def.name, sentence, sentence);
				return;
			}
			if (status === 400 && text && data?.code) {
				const column = data.column && visibleColumns(def).some(col => col.name === data.column) ? data.column : ENTRY_COLUMN;
				landOnEntry(def, op.key, column, text);
				return;
			}
			handleWriteError(error);
		};

		// Is everything on the form besides the groups going as entry
		// operations the way it was read? Then the write has nothing for the
		// PUT, and a colleague's change to another field cannot refuse it.
		const onlyGroupsChanged = (source) => {
			const base = loadedItemRef.current || baseOf(item);
			const names = allElementDefs.filter(def => def?.name && !isRepeatingGroup(def)).map(def => def.name);
			if (names.some(name => !sameValue(source?.elements?.[name], base.elements?.[name]))) return false;
			if (!sameTagIds(handTagIdsOf(source), base.tagIds || [])) return false;
			return !(privateTagsToSend.add.length || privateTagsToSend.remove.length);
		};

		// A write on a card with an ACCOUNT LINK may link or unlink an account
		// (the API denormalises items.accountId from it): the session's `me`
		// answers — the home's "Your record", the Me tooltip, a person's chip
		// becoming their record's badge — are forgotten for this collection,
		// or they would read stale until a reload.
		const forgetLinkedAccounts = () => {
			if (!identityElementOf(allElementDefs)) return;
			refreshMe(collectionId);
			refreshLinkedAccounts(collectionId);
		};
		// A card may require the save to be SIGNED. The API answers 428 with
		// `attestation_required`; the form collects the signature and retries
		// the SAME request with it, so the person types their PIN once, at
		// the moment they are committing the record — which is the only
		// moment a signature means anything.
		const withAttestation = (send) => send().catch(error => {
			if (!needsAttestation(error)) throw error;
			return new Promise((resolve, reject) => {
				setAttestation({
					mode: error?.response?.data?.mode || 'pin',
					onSigned: token => send({ headers: { 'X-Attestation': token } }).then(resolve, reject),
					onCancel: () => reject(error),
				});
			});
		});

		if (!isNewRecord) {
			/*
			 * The update. `PUT /api/items/{id}` MERGES by default (an omitted
			 * key keeps its stored value) and the form still spells out every
			 * declared element, so it never sends `merge`. It does send
			 * `ifUpdatedAt` — the item's `updatedAt` as this form read it —
			 * so a save made while somebody else saved the same item is
			 * refused (409 `stale_item`, with the item as it stands) instead
			 * of silently putting their fields back (delete audit F6). The
			 * refusal is judged field by field against the item as the form
			 * READ it, the card table's rule (staleConflict.js):
			 *   - fields only they changed come into the form, silently;
			 *   - fields only this person changed stay as typed;
			 *   - fields both changed to different values are asked about
			 *     (ItemConflictModal: a row per field, Yours and theirs —
			 *     headed with who changed it and when — chosen per row);
			 * then the save goes again ONCE against the new `updatedAt`. A
			 * second 409 says the change did not land and leaves the form
			 * holding what was typed; an item that is gone (or no longer
			 * readable) is said, never retried.
			 */
			// The groups whose changes go as single-entry operations, in order,
			// each op one call that does not carry the other entries (epic ruling
			// 5): two people editing different entries of one item both land.
			// The baseline of a group is the list this form READ; every op that
			// lands moves it, so a Save pressed again after a refusal sends only
			// what is left. An entry removed by someone else already is as good
			// as removed. A refusal is placed on its entry and stops the save.
			const runEntryOperations = async (plans) => {
				for (const { def, plan } of plans) {
					const idsByKey = {};
					// The list as the SERVER holds it, so a place is found by the entry
					// it follows and a colleague's entries (added or removed since this
					// form read the item) do not throw it off. Read once before the
					// first placed entry, then taken from each door's answer.
					let live = null;
					if (plan.ops.some(op => op.op === 'add' || op.op === 'move')) {
						try {
							const { data: fresh } = await api.get(`${API_ITEMS_URL}/${item._id}`);
							if (Array.isArray(fresh?.elements?.[def.name])) live = fresh.elements[def.name];
						} catch {
							// Not read: the places planned from what this form read stand.
						}
					}
					for (const op of plan.ops) {
						const entryId = op.entryId || idsByKey[op.key] || null;
						if (op.op !== 'add' && !entryId) continue;
						let place = op.position;
						if (live && (op.op === 'add' || op.op === 'move') && op.afterKey !== undefined) {
							const afterId = op.afterKey === null ? null : (idsByKey[op.afterKey] || op.afterKey);
							const found = positionAfter(live, afterId, op.op === 'move' ? entryId : null);
							if (found) place = found;
						}
						const sent = { ...op, entryId, position: place };
						let result = null;
						try {
							result = await withAttestation(config => sendEntryOperation(item._id, def.name, sent, config));
						} catch (error) {
							const data = error?.response?.data;
							if (op.op === 'remove' && error?.response?.status === 404 && data?.code === 'unknown_entry') {
								if (loadedItemRef.current?.elements) loadedItemRef.current.elements[def.name] = applyOperation(loadedItemRef.current.elements[def.name], sent);
								continue;
							}
							placeEntryRefusal(def, op, error);
							const failure = new Error('An entry change was refused.');
							failure.entryOperation = true;
							throw failure;
						}
						takeFormulaWarnings(result?.item);
						if (op.op === 'add') {
							idsByKey[op.key] = result?.entryId;
							setItem(prev => ({
								...prev,
								elements: {
									...prev.elements,
									[def.name]: entriesOf(prev.elements?.[def.name]).map(entry => (
										entryKey(entry) === op.key ? { ...entry, id: result?.entryId, _key: op.key } : entry
									)),
								},
							}));
						}
						if (live) {
							live = Array.isArray(result?.item?.elements?.[def.name])
								? result.item.elements[def.name]
								: applyOperation(live, sent, { entryId: result?.entryId, position: result?.position });
						}
						if (loadedItemRef.current?.elements) {
							loadedItemRef.current.elements[def.name] = applyOperation(loadedItemRef.current.elements[def.name], sent, { entryId: result?.entryId, position: result?.position });
							if (result?.item?.updatedAt) loadedItemRef.current.updatedAt = result.item.updatedAt;
						}
					}
				}
			};

			const sendUpdate = (source, { retried = false } = {}) => {
				// `source` is an audit field (provenance of an ingested item): the
				// API owns it and may refuse an update that carries it.
				const { source: _unusedSource, merge: _unusedMerge, ...updatePayload } = source === item ? payload : wirePayload(source);
				const ifUpdatedAt = loadedItemRef.current?.updatedAt || source?.updatedAt || null;
				if (ifUpdatedAt) updatePayload.ifUpdatedAt = ifUpdatedAt;
				const opPlans = groupDefsOnForm
					.filter(def => groupPlans[def.name]?.mode === 'ops')
					.map(def => ({ def, plan: groupPlans[def.name].plan }));
				// Only entries changed: nothing for the PUT to say, and a colleague's
				// change to some other field is no business of this save.
				const putNeeded = !(opPlans.length && onlyGroupsChanged(source));
				formulaBatches.length = 0;
				formulaFinal.elements = null;
				submittingRef.current = true;
				setSaving('save');
				const written = putNeeded
					? withAttestation(config => api.put(`${API_ITEMS_URL}/${item._id}`, updatePayload, config))
					: Promise.resolve({ status: 200, data: null });
				written
					.then(async ({ status, data }) => {
						if (status !== 200) return;
						takeFormulaWarnings(data);
						if (data && data._id) {
							// What this form read of an operated group stays what it read
							// (the answer holds colleagues' entries too); the ops move it.
							const read = {};
							opPlans.forEach(({ def }) => { read[def.name] = loadedItemRef.current?.elements?.[def.name]; });
							loadedItemRef.current = baseOf(data);
							opPlans.forEach(({ def }) => { loadedItemRef.current.elements[def.name] = read[def.name]; });
							// A group the PUT wrote whole now has the ids keepr issued: the form
							// takes them, so a Save pressed again (another group's entry refused)
							// does not see its new entries as new ones and write them twice.
							groupDefsOnForm.filter(def => groupPlans[def.name]?.mode === 'whole').forEach(def => {
								const written = data.elements?.[def.name];
								if (Array.isArray(written)) setItem(prev => ({ ...prev, elements: { ...prev.elements, [def.name]: written } }));
							});
						}
						await runEntryOperations(opPlans);
						sayDroppedTags(data);
						applyPrivateAfterSave(item._id);
						forgetLinkedAccounts();
						// The uploads it sent are the item's now; the rest go.
						// (A group written as single-entry operations is left out of the PUT,
						// so its entries' uploads are read from the form itself.)
						settleHeldFiles(fileIdsIn({ ...(source?.elements || {}), ...(updatePayload.elements || {}) }));
						message.success('Item saved.');
						sayFormulaWarnings();
						markPristine();
						if (pane) paneLeave.saved(data);
						else navigate(`/collections/${collectionId}/items`);
					})
					.then(() => { submittingRef.current = false; setSaving(null); }, error => {
						// Cleared BEFORE the stale path, which may send again.
						submittingRef.current = false;
						setSaving(null);
						// An entry change the API refused is already on its entry.
						if (error?.entryOperation) {
							setSaveFailed(true);
							return;
						}
						if (isStaleItem(error)) {
							handleStale(error, source, retried);
							return;
						}
						setSaveFailed(true);
						handleWriteError(error);
					});
			};

			const handleStale = (error, sent, retried) => {
				const current = error?.response?.data?.item;
				if (!current || !current._id) {
					message.error('This item was deleted, or you can no longer open it, so your changes were not saved.');
					return;
				}
				if (retried) {
					setSaveFailed(true);
					message.warning('Somebody changed this item again while it was saving, so your changes did not land. The form still holds them — save again to try once more.');
					return;
				}
				const base = loadedItemRef.current || baseOf(item);
				// A group written as single-entry operations is not in this
				// comparison: its changes are judged entry by entry by the API.
				const operated = new Set(groupDefsOnForm.filter(def => groupPlans[def.name]?.mode === 'ops').map(def => def.name));
				const { theirsOnly, conflicts } = sortStaleFields({
					names: allElementDefs.map(def => def?.name).filter(name => name && !operated.has(name)),
					base: base.elements,
					mine: sent?.elements || {},
					theirs: current.elements || {},
				});
				const next = { ...sent, elements: { ...(sent?.elements || {}) }, updatedAt: current.updatedAt };
				theirsOnly.forEach(name => { next.elements[name] = current.elements?.[name]; });
				const conflictRows = conflicts.map(name => {
					const def = allElementDefs.find(entry => entry?.name === name);
					return {
						name,
						label: def?.label?.singular || name,
						mine: conflictValueText(def, sent?.elements?.[name]),
						theirs: conflictValueText(def, current.elements?.[name]),
					};
				});
				// Tags merge as a set: the other person's changes stay, and this
				// person's own additions and removals apply on top of them.
				next.tagIds = applyTagDelta(handTagIdsOf(current), tagDelta(base.tagIds || [], handTagIdsOf(sent)));
				setItem(prev => ({ ...prev, elements: next.elements, tagIds: next.tagIds, tagAutoIds: current.tagAutoIds, tagTitles: { ...(prev.tagTitles || {}), ...(current.tagTitles || {}) } }));

				// The save is now made against the item as it stands, so the
				// base moves to it: a later refusal is judged against it.
				const retryAgainst = (resolved) => {
					loadedItemRef.current = baseOf(current);
					// An operated group's baseline stays what this form read.
					operated.forEach(name => { loadedItemRef.current.elements[name] = base.elements?.[name]; });
					sendUpdate(resolved, { retried: true });
				};
				if (!conflictRows.length) {
					// Nothing this person typed was touched (a recompute, or a
					// field they left alone): saving overwrites nothing. Once.
					retryAgainst(next);
					return;
				}
				setStaleConflict({
					conflicts: conflictRows,
					// Who changed them is read from the item's history from
					// the version this form read (ItemConflictModal).
					item: current,
					since: base?.updatedAt || null,
					onSave: (choices) => {
						const resolved = { ...next, elements: { ...next.elements } };
						conflictRows.forEach(row => {
							if (choices[row.name] !== 'theirs') return;
							resolved.elements[row.name] = current.elements?.[row.name];
						});
						setStaleConflict(null);
						setItem(prev => ({ ...prev, elements: resolved.elements }));
						retryAgainst(resolved);
					},
				});
			};

			sendUpdate(item);
		} else {
			submittingRef.current = true;
			setSaving(addAnother ? 'another' : 'save');
			withAttestation(config => api.post(API_ITEMS_URL, payload, config))
				.then(({ status, data }) => {
					if (status !== 200) return;
					sayDroppedTags(data);
					applyPrivateAfterSave(data?._id);
					forgetLinkedAccounts();
					settleHeldFiles(fileIdsIn(payload.elements));
					message.success('Item saved.');
					takeFormulaWarnings(data);
					sayFormulaWarnings();
					// The draft became this item.
					closeSessionDraft();
					if (addAnother) {
						resetForRepeat();
						return;
					}
					markPristine();
					navigate(`/collections/${collectionId}/items`);
				})
				.catch((error) => { setSaveFailed(true); handleWriteError(error); })
				.finally(() => { submittingRef.current = false; setSaving(null); });
		}
	};

	/*
	 * Cancel and Esc. A new item with a draft behind it (or typing about to
	 * become one) asks "Keep as draft / Discard" — Esc used to delete the
	 * draft outright, which made the one key people press to get out of a
	 * form the one that lost their work. Closing the prompt stays on the form.
	 */
	const [leavePromptOpen, setLeavePromptOpen] = useState(false);
	// In the pane: Save · Discard · Stay instead (KPR-1644; hooks/usePaneFormLeave).
	const paneLeave = usePaneFormLeave({
		on: Boolean(pane), saving, submittingRef, save: () => handleSubmit(false),
		discard: () => { settleHeldFiles([]); markPristine(); },
		close: () => pane?.onCancel?.(), onSaved: data => pane?.onSaved?.(data),
	});
	// A rail link or the back button held by the unsaved-changes guard while
	// the prompt is up: { proceed, reset }, or null when Cancel opened it.
	const pendingLeaveRef = useRef(null);
	const leaveForList = useCallback(() => {
		if (pane) pane.onCancel?.();
		else navigate(`/collections/${collectionId}/items`);
	}, [navigate, collectionId, pane]);
	const hasDraftBehind = useCallback((dirty) => draftsEnabled
		&& (Boolean(sessionDraftIdRef.current) || (draftWorthyRef.current && dirty)), [draftsEnabled]);
	onLeaveBlockedRef.current = ({ proceed, reset }) => {
		if (pane) return paneLeave.onBlocked({ proceed, reset });
		if (!hasDraftBehind(true)) return false;
		pendingLeaveRef.current = { proceed, reset };
		setLeavePromptOpen(true);
		return true;
	};
	const handleCancel = useCallback(() => {
		if (pane) return paneLeave.cancel(isDirty);
		if (hasDraftBehind(isDirty)) {
			pendingLeaveRef.current = null;
			setLeavePromptOpen(true);
			return;
		}
		// Anything else typed asks the platform's one question first.
		guardDiscard(leaveForList);
	}, [hasDraftBehind, isDirty, guardDiscard, leaveForList, pane, paneLeave]);
	const finishLeave = useCallback(() => {
		const pending = pendingLeaveRef.current;
		pendingLeaveRef.current = null;
		if (pending) pending.proceed();
		else leaveForList();
	}, [leaveForList]);
	const stayOnForm = useCallback(() => {
		setLeavePromptOpen(false);
		const pending = pendingLeaveRef.current;
		pendingLeaveRef.current = null;
		if (pending) pending.reset();
	}, []);
	const keepDraftAndLeave = useCallback(() => {
		setLeavePromptOpen(false);
		flushDraft();
		markPristine();
		finishLeave();
	}, [flushDraft, markPristine, finishLeave]);
	// Discard throws away what THIS form did, by the Replace rule
	// (dropFormDraft): a draft it made of typing is deleted; a draft it was
	// opened from — the Drafts page's Open, a chip's Apply — stays, put back
	// as it landed if autosave has written typing into it since. It used to
	// end the session draft outright, so Esc → Discard on a draft someone had
	// only opened deleted it (the review of the live-pass merges).
	const discardDraftAndLeave = useCallback(() => {
		setLeavePromptOpen(false);
		dropFormDraft();
		// What this form uploaded goes with what it typed (H1).
		settleHeldFiles([]);
		markPristine();
		finishLeave();
	}, [dropFormDraft, settleHeldFiles, markPristine, finishLeave]);

	// Leaving by any other door — Cancel, the rail, the back button, closing
	// the pane — settles the uploads the save never claimed (H1): an edit
	// keeps none (nothing can reopen them); a new item whose draft is kept
	// keeps the ones its fields hold, since the draft holds them too.
	const leaveHeldFilesRef = useRef(null);
	leaveHeldFilesRef.current = () => {
		const draftKept = draftsEnabled && (Boolean(sessionDraftIdRef.current) || draftWorthyRef.current);
		settleHeldFiles(draftKept ? fileIdsIn(formSnapshotRef.current?.elements) : []);
	};
	useEffect(() => () => leaveHeldFilesRef.current?.(), []);

	// The same three, said out loud in ⋯ → Keyboard shortcuts — registered
	// here, beside the handler, so neither can drift from the other.
	useScreenShortcuts('Item form', [
		{ keys: ['mod', 'enter'], label: 'Save' },
		...(isNewRecord ? [{ keys: ['mod', 'shift', 'enter'], label: 'Save and add another' }] : []),
		{ keys: ['esc'], label: 'Cancel' },
	]);

	// Form shortcuts: ⌘↵ save · ⌘⇧↵ save & add another · Esc cancel.
	const shortcutRef = useRef(null);
	shortcutRef.current = { handleSubmit, handleCancel, isNewRecord };
	useEffect(() => {
		const handler = (event) => {
			const current = shortcutRef.current;
			if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
				event.preventDefault();
				current.handleSubmit(event.shiftKey && current.isNewRecord);
			} else if (event.key === 'Escape') {
				// Esc belongs to an OPEN overlay first — only an open one: a
				// closed modal antd keeps mounted used to swallow every Esc
				// after it (utils/escapeOwner).
				if (escapeBelongsElsewhere(event)) return;
				event.preventDefault();
				current.handleCancel();
			}
		};
		// ⌘↵ inside a rich-text field is caught on the way DOWN: TipTap binds
		// Mod-Enter to a hard break, and ProseMirror's listener on the editable
		// runs before a bubbling one, so the saved markdown used to gain a
		// trailing line break (the table's cell editor had the same bug).
		const richTextSave = (event) => {
			if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey) || event.isComposing) return;
			const target = event.target instanceof Element ? event.target : null;
			if (!target?.closest('[contenteditable="true"]')) return;
			event.preventDefault();
			event.stopPropagation();
			const current = shortcutRef.current;
			current.handleSubmit(event.shiftKey && current.isNewRecord);
		};
		document.addEventListener('keydown', richTextSave, true);
		document.addEventListener('keydown', handler);
		return () => {
			document.removeEventListener('keydown', richTextSave, true);
			document.removeEventListener('keydown', handler);
		};
	}, []);

	// The ⋯ menu's Delete was confirmed and succeeded: leave for the list.
	const handleDeleted = useCallback(() => {
		markPristine();
		if (pane) pane.onDeleted?.();
		else navigate(`/collections/${collectionId}/items`);
	}, [collectionId, markPristine, navigate, pane]);

	// Fields render in white panels. With a FORM LAYOUT (the card's own, or
	// the nearest ancestor's) its groups decide the panels and the order
	// within them, and every element it does not place falls into a trailing
	// "Other fields" panel (utils/formLayout formLayoutPanels). Without one
	// they group AUTOMATICALLY by the element's optional `options.group`,
	// ungrouped fields in a default panel, in definition order across the
	// chain (parent first, a re-declared name once in its ancestor's slot)
	// — an element set's elements under the set's name. `def` is the card
	// that supplied the winning definition.
	//
	// A date RANGE is two fields here, each with its own label, place and
	// width (KPR-626, the owner's k01): only the read surfaces draw the pair as
	// one value. An end the layout does not place sits right after its start
	// (k02, utils/formLayout placeRangeEnds); with no layout, the definition
	// order and the element's own group decide, as for any element.
	//
	// An element the layout LEAVES OUT (KPR-1211, utils/formLayout
	// leftOutNames — never one the save can demand) is not drawn; a value it
	// holds stays on the record, since the payload starts from the item as it
	// was read. One that holds a refusal is drawn, in the "Left out of the
	// form" panel, so a save is never blocked by a field nobody can see.
	const fieldPanels = useMemo(() => {
		const visible = mergeChainEntries(cardDefChain)
			.filter(({ element }) => !isHiddenFromMe(element))
			.map(({ element, card }) => ({ element, def: card }));
		const leftOut = leftOutNames(formLayout?.body, visible.map(entry => entry.element));
		const flat = leftOut.size
			? visible.filter(({ element }) => !leftOut.has(element?.name) || validationErrors[element.name])
			: visible;
		const getElement = entry => entry.element;
		const layoutBody = placeRangeEnds(formLayout?.body, flat.map(getElement));
		const panels = formLayoutPanels(flat, layoutBody, { getElement, surface: 'form' })
			|| groupElementsIntoPanels(flat, { getElement }).map(panel => ({ ...panel, description: null, collapsed: false }));
		return panels.map(panel => ({
			key: panel.key,
			label: panel.label,
			description: panel.description,
			collapsed: panel.collapsed,
			// What each field asks of the grid (its layout entry's width and
			// new-row flag; the automatic grouping asks for nothing), already
			// held to the element's type limits — the same call the read view
			// makes, so the two faces draw one width.
			fields: panel.entries.map((entry, index) => {
				const { span, newRow } = panel.placements?.[index] || AUTO_PLACEMENT;
				// A repeating group is a list of entries: it takes the whole row,
				// unless the layout asked for a width (the table has no row for it).
				if (isRepeatingGroup(entry.element)) {
					const wide = Number.isInteger(span) ? cellSpans(entry.element, span) : { wide: 12, medium: 6 };
					return { ...entry, cell: { ...cellSpans(entry.element, span), ...wide, newRow: true } };
				}
				return { ...entry, cell: { ...cellSpans(entry.element, span), newRow } };
			}),
		}));
	}, [cardDefChain, formLayout, validationErrors]);
	// The element names in the order they are on screen, for the submit's
	// scroll to the first refused field.
	const renderedOrderRef = useRef([]);
	renderedOrderRef.current = fieldPanels.flatMap(panel => panel.fields.map(({ element }) => element?.name).filter(Boolean));
	// Which panel each rendered element is in, for opening the ones a save
	// attempt has something to say about.
	const panelOfFieldRef = useRef(new Map());
	panelOfFieldRef.current = new Map(fieldPanels.flatMap(panel => panel.fields.map(({ element }) => element?.name).filter(Boolean).map(name => [name, panel.key])));
	// A group the layout collapses by default opens on a click, and on its
	// own on every save attempt that finds a field in it to fix. It is NOT
	// held open while the error stands: its author may close it again (to
	// fill the fields above first); the next attempt reopens it.
	const [panelOpen, setPanelOpen] = useState({});
	// useId's colons stay out of the DOM ids: a selector built from an id with
	// colons in it is not one (jsdom's :has() lookup builds one from the id).
	const panelUid = `form-panel-${useId().replace(/:/g, '')}`;
	const revealFields = useCallback((names) => {
		const keys = new Set(names.map(name => panelOfFieldRef.current.get(name)).filter(Boolean));
		if (!keys.size) return;
		setPanelOpen(prev => {
			const next = { ...prev };
			keys.forEach(key => { next[key] = true; });
			return next;
		});
	}, []);
	// A scroll to a field runs AFTER the render that opened its group — an
	// effect, so the target is out of its `hidden` container by then. A new
	// object each time, so a second attempt at the same field scrolls again.
	const [scrollTarget, setScrollTarget] = useState(null);
	const scrollToField = useCallback((name) => setScrollTarget({ name }), []);
	// …and the first refused field takes FOCUS, so the keyboard lands where the
	// fix is (KPR-581): the first control inside it that can be typed into. A
	// field with none (a disabled one, a custom widget) keeps the scroll alone.
	useEffect(() => {
		if (!scrollTarget) return;
		const field = document.getElementById(`element-field-${scrollTarget.name}`);
		if (!field) return;
		field.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
		firstFocusable(field)?.focus?.({ preventScroll: true });
	}, [scrollTarget]);
	const fieldsLoading = itemLoading || cardLoading || (!!cardDefId && !formLayoutLoaded);

	// The form's "person": its first card-lookup. The side card prefers the
	// last item for whoever is selected there.
	const personElement = useMemo(
		() => allElementDefs.find(def => def?.dataType === 'card-lookup') || null,
		[allElementDefs],
	);

	const leafCardDef = cardDef;
	const shareElementDefs = allElementDefs;

	const titleSingular = leafCardDef?.name || leafCardDef?.label?.singular || 'Item';
	const resolvedCardId = useMemo(
		() => normalizeId(item.card_id) || normalizeId(leafCardDef?._id) || normalizeId(effectiveCardId),
		[item.card_id, leafCardDef?._id, effectiveCardId]
	);
	const cardVisual = useMemo(() => ({
		icon: leafCardDef?.icon,
		color: leafCardDef?.color,
		name: titleSingular,
	}), [leafCardDef?.icon, leafCardDef?.color, titleSingular]);

	const resolvedItemId = useMemo(() => {
		const stateId = normalizeId(item?._id);
		if (stateId && stateId !== '-1') return stateId;
		const routeId = normalizeId(itemId);
		if (routeId && routeId !== '-1') return routeId;
		return null;
	}, [item?._id, itemId]);

	// A move to another item is a new visit: the errors go, and every section
	// goes back to where the layout starts it. A person's own fold is never
	// kept (KPR-581).
	useEffect(() => {
		setValidationErrors({});
		setPanelOpen({});
	}, [itemId]);

	const currentItemId = resolvedItemId;

	// The title can template a lookup, whose own title can template another.
	// useLinkedItems batch-fetches the referenced items (seeded with this one, so
	// unsaved edits show through) and the shared resolver walks them.
	const lookupSeed = useMemo(() => (item ? [item] : []), [item]);

	const lookupItemsById = useLinkedItems({
		items: lookupSeed,
		getElementsForCard,
		enabled: !cardDefinitionsLoading,
		resetKey: itemId,
	});

	// An item that references itself resolves to '' rather than recursing into
	// its own template to build its own title.
	const suppressLookupIds = useMemo(
		() => new Set(currentItemId ? [currentItemId] : []),
		[currentItemId]
	);

	const resolveLookupValue = useMemo(() => createLookupResolver({
		itemsById: lookupItemsById,
		cardsById,
		getElementsForCard,
		suppressIds: suppressLookupIds,
	}), [lookupItemsById, cardsById, getElementsForCard, suppressLookupIds]);

	// A draft chip's popover: the fields the draft sets, as text.
	const describeDraft = useCallback(draft => draftContentRows({
		elementDefs: allElementDefs,
		defaults: newItemDefaults(),
		draft,
	}).map(row => ({ key: row.key, label: row.label, text: contentRowText(row, { resolveLookupValue }) })),
	[allElementDefs, newItemDefaults, resolveLookupValue]);

	// The title and crumb never show a raw id: a picked lookup whose target
	// has not loaded yet reads as an empty lookup ("[Vehicle]") until its name
	// arrives. The other consumers keep the plain resolver.
	const titleLookupValue = useMemo(() => omitUnresolvedLookups(resolveLookupValue), [resolveLookupValue]);
	const itemDisplayValue = useMemo(() => {
		return getItemDisplayValue({
			card: leafCardDef,
			elementDefs: shareElementDefs,
			item,
			resolveLookupValue: titleLookupValue,
		});
	}, [leafCardDef, shareElementDefs, item, titleLookupValue]);

	// The shell's last crumb: the item's display value (truncated there, full
	// text in a tooltip), or "New {card name}" on the add form.
	useShellSlot({ crumbTail: (isNewRecord ? `New ${titleSingular}` : itemDisplayValue) || null, off: Boolean(pane) });

	// Honest rendering from the server-computed myAccess: readers (and
	// card-scoped readers on this card) get a read-only form — no Save, no
	// Delete, disabled inputs. The server enforces the same rules. Gated on the
	// LOADED collection so writers don't get a disabled flash (or a stuck
	// read-only editor if the collection fetch fails — the server still
	// enforces on save).
	const readOnly = !isNewRecord
		&& Boolean(collectionAccess.collection)
		&& !collectionAccess.canWriteCard(resolvedCardId);

	// The record lock, read through the card's chain as the API reads it
	// (utils/recordPolicy). A new item is never locked. A locked record says
	// so BEFORE Save (KPR-155): its fields are read-only, there is no Save,
	// and the notice above the fields says why and what can be done — the
	// rule's own reason for existing. The API refuses a late edit either way
	// (409 item_locked), and that refusal closes the form too (lockRefusal).
	const recordPolicy = useMemo(() => recordPolicyForChain(cardDefChain), [cardDefChain]);
	const lockState = useMemo(
		() => (isNewRecord ? { locked: false } : lockStateFor(item, recordPolicy)),
		// lockClock: read again when the lock's next boundary passes.
		[isNewRecord, item, recordPolicy, lockClock] // eslint-disable-line react-hooks/exhaustive-deps
	);
	const recordLocked = !isNewRecord && (lockState.locked || Boolean(lockRefusal));
	// Only a writer's form changes state: a reader's is read-only already.
	const recordClosed = recordLocked && !readOnly;
	// Fields, tags and Save follow both: who may write, and whether the record is open.
	const fieldsReadOnly = readOnly || recordClosed;
	const canManageRecord = Boolean(collectionAccess.canManage);
	// The next moment the answer changes: the unlock's end, or the grace
	// period's. A timeout past ~24 days overflows; nobody keeps a form open
	// that long.
	const lockBoundary = lockState.unlocked
		? lockState.until?.getTime()
		: (lockState.within && item?.createdAt
			? new Date(item.createdAt).getTime() + (Number(recordPolicy?.lock?.minutes) || 0) * 60000
			: null);
	useEffect(() => {
		if (!lockBoundary || Number.isNaN(lockBoundary)) return undefined;
		const wait = lockBoundary - Date.now() + 1000;
		if (wait > 2 ** 31 - 1) return undefined;
		const timer = setTimeout(() => setLockClock(tick => tick + 1), Math.max(wait, 0));
		return () => clearTimeout(timer);
	}, [lockBoundary]);

	// A manager unlocked the record from this form (the notice or the ⋯).
	// Clean, the form reads the item again (Refresh's path) and opens. Holding
	// unsaved edits — a save the lock refused — it takes the unlock's window
	// from the answer instead, so nothing typed is lost and Save goes through.
	const handleUnlocked = useCallback((result) => {
		setLockRefusal(null);
		if (isDirty && result?.unlockedUntil) {
			setItem(prev => ({ ...prev, unlockedUntil: result.unlockedUntil }));
			return;
		}
		readItemAgain();
	}, [isDirty, readItemAgain]);

	// The elements this person cannot change because a group built from
	// records reads them (owner ruling b79 and its note): read once per card
	// and shown read-only BEFORE Save, with who can help — instead of a
	// refusal after it. The server still decides on save; a failed read just
	// leaves the form as it was. Readers get nothing (their form is
	// read-only already).
	const [lockedElements, setLockedElements] = useState(NO_LOCKS);
	useEffect(() => {
		if (readOnly || !collectionId || !resolvedCardId) {
			setLockedElements(NO_LOCKS);
			return undefined;
		}
		let live = true;
		getLockedElements(collectionId, resolvedCardId)
			.then(list => { if (live) setLockedElements(list.length ? list : NO_LOCKS); })
			.catch(() => { if (live) setLockedElements(NO_LOCKS); });
		return () => { live = false; };
	}, [readOnly, collectionId, resolvedCardId]);
	// Whether the record AS STORED links an account (b78's locks apply only
	// then): its accountId, or — written before the element became the link —
	// the identity element's loaded value.
	const identityName = identityElementOf(allElementDefs)?.name || null;
	const storedLinked = Boolean(item?.accountId)
		|| Boolean(identityName && loadedItemRef.current?.elements?.[identityName]);
	// An unreviewed submission is read by no rule, so nothing on it is locked.
	const pendingIntake = item?.intake?.reviewState === 'pending';
	const isLocked = useCallback(name => {
		if (pendingIntake) return false;
		const lock = lockedElements.find(entry => entry.name === name);
		if (!lock) return false;
		if (lock.when === 'always') return true;
		if (lock.when === 'edit') return !isNewRecord;
		return lock.when === 'linked' && !isNewRecord && storedLinked;
	}, [lockedElements, isNewRecord, storedLinked, pendingIntake]);

	// The full platform action set, same as the list and the pane; only "Open
	// item" is dropped — you are on it. Drawn by the context bar's one ⋯.
	const itemActions = useItemActions({
		surface: 'form',
		item,
		card: leafCardDef,
		elementDefs: shareElementDefs,
		resolveLookupValue,
		title: itemDisplayValue,
		collectionId,
		collection,
		canEdit: !readOnly,
		// Unlock… is a manager's; the record policy reads through the card's
		// parents from the same cache the form does (KPR-155).
		canManage: canManageRecord,
		cardsById,
		onDeleted: handleDeleted,
		onRelated: () => setRelatedRefreshKey(key => key + 1),
		onUnlocked: handleUnlocked,
		// A save the lock refused closes the record here whatever its dates say.
		locked: isNewRecord ? null : recordLocked,
		// The form's tags save with it: Tag… opens its own row.
		onOpenTags: openFormTags,
		// A card action changed the record on the server: read it again, unless
		// there are unsaved edits that a re-read would throw away (the save then
		// meets the conflict check, as it would for a colleague's change).
		onPressed: () => { if (!isDirty) setItemAttempt(count => count + 1); },
	});

	// THE CARD'S ACTIONS AS HEADER BUTTONS (KPR-1342; the owner's ruling,
	// knowledge/rules/card-action-buttons-ruling.md): on the item page only,
	// each action that applies to this record — one without a condition, or
	// one the record's answer says holds (useItemActions `headerActions`) — as
	// a labeled secondary, in the card's order. platform-standards §3 caps a
	// header at one filled primary and two labeled secondaries, and Cancel is
	// one of them, so the buttons take what is left; the rest are in the ⋯,
	// which lists every action anyway. When the record changes so a condition
	// stops holding (a press, a Refresh, a save), the record's answer is read
	// again and the button leaves. In a compact window (§18) they fold into the
	// ⋯, as every header's secondaries do.
	const { compact: onPhone } = useWindowClass();
	const secondariesUsed = fieldsReadOnly || itemGone ? 0 : 1; // Cancel
	const headerActionButtons = onPhone || itemGone
		? []
		: (itemActions.headerActions || []).slice(0, Math.max(0, 2 - secondariesUsed)).map(entry => (
			<Button key={entry.key} className="item-form-card-action" icon={entry.icon} onClick={entry.onClick}>
				{entry.label}
			</Button>
		));

	// The form's actions, defined once: the context bar shows them at the top
	// (beside the draft note and the ⋯ menu), FormActionsFooter repeats the
	// same node at the end of the main column. Readers get neither.
	const formActions = fieldsReadOnly ? null : (
		<>
			<Button onClick={handleCancel}>Cancel</Button>
			{isNewRecord ? (
				// A press while a write is in flight is ignored (handleSubmit);
				// the control that was pressed stays enabled, so it keeps focus
				// and shows the wait in place. Nothing dims.
				<Button
					className={`item-form-save-another${saving === 'another' ? ' is-pending' : ''}`}
					onClick={() => handleSubmit(true)}
					disabled={savingDisabled || !isDirty || saving === 'save'}
					aria-busy={saving === 'another' || undefined}
					aria-disabled={saving === 'another' || undefined}
				>
					{saving === 'another' ? <><Spinner size="sm" label={null} /> Saving</> : 'Save & add another'}
				</Button>
			) : null}
			<Button
				type="primary"
				className={`item-form-save${saving === 'save' ? ' is-pending' : ''}`}
				onClick={() => handleSubmit(false)}
				disabled={savingDisabled || !isDirty || saving === 'another'}
				aria-busy={saving === 'save' || undefined}
				aria-disabled={saving === 'save' || undefined}
			>
				{saving === 'save' ? <><Spinner size="sm" label={null} /> Saving</> : 'Save'}
			</Button>
		</>
	);

	return (
		<HeldFilesContext.Provider value={heldFiles.value}>
			<div className={`item-form-page${pane ? ' item-form-page--pane' : ''}`}>
				{/* The item's own head (ItemFormHeader): the context bar on the
				    item page; in the pane, the card's buttons and the form's ⋯ in
				    the pane's title row. */}
				<ItemFormHeader
					paneSlot={pane ? pane.headerSlot || null : undefined}
					titleSingular={titleSingular}
					cardVisual={cardVisual}
					title={itemDisplayValue}
					item={item}
					isNewRecord={isNewRecord}
					templateId={templateId}
					isProfileLaunch={isProfileLaunch}
					quickAddName={quickAddName}
					itemActions={itemActions}
					draftStatus={draftStatus}
					refreshing={itemRefreshing}
					onRefresh={handleRefresh}
					itemLoading={itemLoading}
					actionButtons={headerActionButtons}
					formActions={formActions}
					itemGone={itemGone}
				/>

				{itemActions.modals}

				{/* A save that did not land. The form still holds everything that
				    was typed; Retry sends the same save again. */}
				{saveFailed ? (
					<Alert
						className="item-form-page__alert item-form__save-failed"
						type="error"
						showIcon
						message="The item was not saved"
						description="Your changes are still here."
						action={<Button size="small" onClick={() => handleSubmit(lastAddAnotherRef.current)}>Retry</Button>}
					/>
				) : null}

				{/* A Refresh that did not land: the form is as it was. */}
				{refreshFailed && !itemGone ? (
					<Alert
						className="item-form-page__alert item-form__refresh-failed"
						type="warning"
						showIcon
						message="The item could not be refreshed"
						description="What is shown is from before."
						action={<Button size="small" onClick={handleRefresh}>Retry</Button>}
					/>
				) : null}

				<PlanLimitAlert refusal={planLimit} onClose={() => setPlanLimit(null)} className="item-form-page__alert" />

				{/* A locked record says so before Save (KPR-155), with what can
				    be done: Unlock for a manager, Correct for anyone who can
				    edit it. An unlocked one says until when. */}
				{recordClosed && !itemGone ? (
					<RecordLockNotice
						reason={lockState.locked ? lockState.reason : lockSentence(lockRefusal)}
						canManage={canManageRecord}
						canCorrect={!item?.supersededBy}
						onUnlock={() => itemActions.run('unlock')}
						onCorrect={() => itemActions.run('correct')}
					/>
				) : null}
				{!recordClosed && !readOnly && lockState.unlocked && !itemGone ? (
					<Alert
						className="item-form-page__alert item-form__record-unlocked"
						type="info"
						showIcon
						role="status"
						message={`Unlocked until ${formatDisplayDateTime(lockState.until)}. The next save closes it again.`}
					/>
				) : null}

				{readOnly ? null : <GlobalLiveNotice collection={collection} className="item-form-page__alert" />}
				{/* Every record of a global collection, read or edited: a reader
				    must not reach one without the words the owner attached. */}
				<GlobalDisclaimer collection={collection} className="item-form-page__alert" />

				{/* What a public form's visitor sent privately (KPR-389) — for
				    this collection's managers alone, beside the submission and
				    never in its fields. Renders nothing for anyone else. */}
				{!isNewRecord ? (
					<IntakePrivatePanel item={item} collection={collection} elementDefs={allElementDefs} className="item-form-page__alert" />
				) : null}

				{draftsEnabled ? (
					<DraftChips
						drafts={chipDrafts}
						loadDraft={loadDraftForForm}
						describe={describeDraft}
						onApply={requestApplyDraft}
						onDelete={deleteChipDraft}
						busyId={applyingDraftId}
					/>
				) : null}

				{/* An item deleted, or no longer readable, since it was opened
				    (found by Refresh, KPR-1346): said in the form's place, never
				    the old copy. */}
				{itemGone ? (
					<Alert
						className="item-form-page__alert item-form__gone"
						type="warning"
						showIcon
						role="status"
						message="This item was deleted, or you can no longer see it"
						description="It changed since you opened it. Go back to the list to see what is there now."
						action={<Button size="small" onClick={leaveForList}>Go to the list</Button>}
					/>
				) : (
				<div className="item-form-columns item-form-columns--wide">
					{/* A person's own hand on the form (not a prefill or a
					    template, which call markDirty too) is what freezes a
					    late-arriving layout out of this open. */}
					<Form className="item-form-main" disabled={fieldsReadOnly} onPointerDownCapture={noteInteraction} onKeyDownCapture={noteInteraction} onChangeCapture={noteInteraction}>
						{!isNewRecord && !fieldsLoading ? <LinkedEntryNotice groupDefs={allElementDefs} elements={loadedItemRef.current?.elements} /> : null}
						<DataState
							className="item-form__fields"
							state={itemError ? DATA_STATE.ERROR : fieldsLoading ? DATA_STATE.LOADING : DATA_STATE.READY}
							skeleton={<Skeleton variant="form-field" count={6} />}
							onRetry={() => setItemAttempt(n => n + 1)}
							label="the item"
						>
						{fieldsLoading
							? null
							: (fieldPanels.length ? fieldPanels : [{ key: '__empty__', label: null, fields: [] }]).map((panel, index, list) => {
								// A section with a heading IS a disclosure, whatever its
								// layout says about the start (KPR-581): the layout only
								// decides where it begins, and a person's own fold is
								// not kept past this visit. A lone untitled panel has no
								// heading, so nothing to fold.
								const headed = !!(panel.label || list.length > 1 || panel.collapsed);
								const open = !headed || (panelOpen[panel.key] ?? !panel.collapsed);
								const kicker = panel.label || cardDef?.name || 'Details';
								const bodyId = `${panelUid}-${index}`;
								// What the closed heading says (utils/sectionSummary),
								// live from the values as they stand.
								const summary = headed && !open
									? sectionSummary(panel.fields, item.elements, { errors: validationErrors, attachmentsOff: attachmentsOn === false })
									: null;
								return (
								<section className={`form-panel${headed ? ' form-panel--collapsible' : ''}`} key={panel.key} data-panel={panel.key}>
									{headed ? (
										// The heading IS the disclosure — the Comments section's
										// grammar. A real button: Enter and Space toggle it.
										<button
											type="button"
											className={`form-panel__kicker-toggle${summary?.invalid ? ' form-panel__kicker-toggle--attention' : ''}`}
											aria-expanded={open}
											aria-controls={bodyId}
											onClick={() => setPanelOpen(prev => ({ ...prev, [panel.key]: !open }))}
										>
											<CHEVRON_RIGHT className="form-panel__kicker-chevron" aria-hidden="true" />
											<span className="micro-label form-panel__kicker">{kicker}</span>
											{summary ? <SectionHeaderStatus summary={summary} surface="form" /> : null}
										</button>
									) : null}
									{panel.description && open ? <p className="form-panel__hint form-panel__description">{panel.description}</p> : null}
									{/* Collapsed fields stay MOUNTED, only hidden: a value
									    typed into one is item state either way, and a field
									    whose inputs unmount would drop its own half-typed
									    text (DateField). */}
									<FormGrid className="item-form-fields" id={bodyId} hidden={!open}>
										{panel.fields.map(({ element, def, cell }) => {
											// A field a group built from records reads, for this
											// person: shown as it is, disabled, with why and who
											// can change it (owner ruling b79's note).
											const locked = isLocked(element.name);
											// A value an element rule holds this person to
											// "see only" (KPR-509): shown as it is, disabled,
											// with the one sentence and the door to who can
											// change it. The API decides on save either way.
											const seeOnly = !readOnly && isSeeOnlyElement(element);
											const held = locked || seeOnly;
											// The END of a date range says how long the span
											// its two fields describe is ("3 days"), under it.
											const rangeStart = rangeStartFor(element.name, allElementDefs);
											const field = (
												<EventFormElement
													event={item}
													element={element}
													// A see-only element on a new item shows what the
													// item will get — its default — whatever a seed put
													// there (the save sends it blank, above).
													value={seeOnly && isNewRecord ? getElementInitialValue(element, { isNewRecord: true, zone }) : item.elements?.[element.name]}
													onChange={handleFEChange}
													cardMeta={def}
													showIconBadge={false}
													errorMessage={validationErrors[element.name]}
													siblingDefs={allElementDefs}
													storedElements={isNewRecord ? null : (loadedItemRef.current?.elements || null)}
													// A card this person cannot write, or a locked record
													// (KPR-155), is drawn read-only: no input, and a repeating
													// group has no Add, Reorder or Remove. Said per field: every
													// input passes its own `disabled`, which antd's Form context
													// cannot override, so the Form's `disabled` alone left them open.
													disabled={held || fieldsReadOnly}
													attachmentsOn={attachmentsOn}
													entryErrors={validationErrors}
													resetKey={repeatEpoch}
													zone={zone}
													resolveLookupValue={resolveLookupValue}
												/>
											);
											return (
												<FormGridCell
													key={`${normalizeId(def?._id) || 'def'}-${element.name}`}
													className={`item-form-field${held ? ' item-form-field--locked' : ''}`}
													wide={cell.wide}
													medium={cell.medium}
													newRow={cell.newRow}
													data-access={seeOnly ? 'see' : undefined}
												>
													{field}
													{rangeStart ? (
														<RangeSpanHint start={rangeStart} end={element} values={item.elements} />
													) : null}
													{locked ? <AccessLockNotice collectionId={collectionId} /> : null}
													{!locked && seeOnly ? <GrantReadOnlyNotice collectionId={collectionId} /> : null}
												</FormGridCell>
											);
										})}
									</FormGrid>
									{/* The last panel closes with the item's context: its
									    tags (the chips and "+ Add tag", saved with the form —
									    docs/plans/tags.md §5.2). */}
									{index === list.length - 1 ? (
										<>
											<div ref={tagRowRef} className="item-form-tags-slot">
												<ItemFormTags
													item={item}
													collection={collection}
													canEdit={!fieldsReadOnly}
													onChange={handleTagsChange}
													pickerOpen={tagPickerOpen}
													onPickerOpenChange={setTagPickerOpen}
													note={launchTagNote}
													onDismissNote={() => setLaunchTagNote(null)}
													activeTagId={isNewRecord && activeTagOnForm && activeTag?.tagId === activeTagOnForm ? activeTagOnForm : null}
												/>
											</div>
										</>
									) : null}
								</section>
								);
							})
						}
						</DataState>
						{/* Comments is its own section AFTER every element panel —
						    the item's journal, not one of its elements, so it never
						    joins a group. It saves on its own (each comment is
						    posted, edited or deleted as it is written), so it is no
						    part of this form's dirty state, draft or Save. */}
						{(fieldsLoading || itemError || !commentsAllowed) ? null : (
							<ItemComments
								key={isNewRecord ? 'new' : itemId}
								itemId={isNewRecord ? null : itemId}
								variant="form"
								defaultOpen={commentsExpandedDefault}
								className="form-panel item-form-comments"
							/>
						)}
						{fieldsLoading || itemError ? null : pane ? (
							<ItemFormPaneFooter slot={pane.footerSlot} formActions={formActions} onCancel={handleCancel} />
						) : <FormActionsFooter>{formActions}</FormActionsFooter>}
					</Form>

					<aside className="item-form-side">
						{isNewRecord ? (
							<LastItemCard
								collectionId={collectionId}
								cardId={resolvedCardId}
								personElementName={personElement?.name}
								personValue={personElement ? item.elements?.[personElement.name] : undefined}
							/>
						) : null}

						{/* Attachments are a COLLECTION-level capability, not a
						    card one: the API gates uploads on
						    collection.allowAttachments, so the UI must ask the
						    same question or it would offer an upload the server
						    answers with a 403. Uploads/removals save on the
						    spot, so the section needs the resolved SAVED item
						    id — a new item shows its own save-first guard. */}
						{collection?.allowAttachments === true ? (
							<section className="form-panel">
								{/* The section renders its own kicker — one "Attachments". */}
								<AttachmentsSection
									subjectType="item"
									subjectId={resolvedItemId}
									readOnly={readOnly}
									locked={recordLocked}
									canDownload={canTakeCopies(collection)}
								/>
							</section>
						) : null}

						{/* Dates and provenance, once the item exists (KPR-579). */}
						{isNewRecord ? null : <ItemInfoCard item={item} />}

						<div className="item-form-shortcuts">
							<span><kbd>⌘↵</kbd> Save</span>
							{isNewRecord ? <span><kbd>⌘⇧↵</kbd> Save & add another</span> : null}
							<span><kbd>Esc</kbd> Cancel</span>
						</div>
					</aside>
				</div>
				)}
			</div>

			{/* Relationships are their own records, saved on the spot — they sit
			    outside the form shell so the form ends at its Save button.
			    Both origins, as the read view and the detail pane show them: a
			    derived row does restate a lookup field above, but hiding them
			    made a card-lookup item read "No related items" here while the
			    pane listed three. Derived rows reflect the last *saved* element
			    values (they are read-only, so an in-progress lookup edit shows
			    up after Save); saving navigates back to the items list, so
			    there is nothing to refresh in place. */}
			{itemGone ? null : (
				<RelatedItemsSection
					itemId={resolvedItemId}
					collectionId={collectionId}
					item={item}
					disabled={readOnly}
					refreshKey={relatedRefreshKey}
				/>
			)}

			{paneLeave.prompt}

			{/* Cancel / Esc on a new item with a draft behind it. The close X,
			    the mask and Esc all mean "stay on the form". */}
			<Modal
				open={leavePromptOpen}
				title="Keep this as a draft?"
				onCancel={stayOnForm}
				footer={[
					<Button key="keep" type="primary" onClick={keepDraftAndLeave}>Keep as draft</Button>,
					<Button key="discard" danger onClick={discardDraftAndLeave}>Discard</Button>,
				]}
			>
				<p className="item-form-leave-prompt">
					A draft waits at the top of the next new {titleSingular} form, and under Drafts in your account menu, for 30 days.
					{leavePromptOpen && sessionDraftId && appliedDraftRef.current?.id === sessionDraftId
						? ' Discard leaves the draft as it was when you opened it.'
						: null}
				</p>
			</Modal>

			{/* A draft chip's Apply over something the form holds: what
			    changes, field by field, and whether to keep the form's
			    contents as a draft first. */}
			<DraftReplaceModal
				open={Boolean(replaceAsk)}
				draft={replaceAsk?.draft}
				rows={replaceAsk?.rows || NO_CONFLICTS}
				resolveLookupValue={resolveLookupValue}
				busy={replaceBusy}
				onReplace={() => answerReplace(false)}
				onKeepMine={() => answerReplace(true)}
				onCancel={() => setReplaceAsk(null)}
			/>

			{/* Only ever mounted by a 409 stale_item that touched a field this
			    person changed too: which value to keep, per field. */}
			<ItemConflictModal
				open={Boolean(staleConflict)}
				conflicts={staleConflict?.conflicts || NO_CONFLICTS}
				item={staleConflict?.item || null}
				since={staleConflict?.since || null}
				onSave={choices => staleConflict?.onSave(choices)}
				onCancel={() => setStaleConflict(null)}
			/>

			{/* Only ever mounted by a 428: the card asked for a signature and
			    the save is waiting on it. */}
			{attestation ? (
				<AttestationModal
					open
					mode={attestation.mode}
					collectionId={collectionId}
					cardId={normalizeId(item?.card_id)}
					onSigned={token => { const { onSigned } = attestation; setAttestation(null); onSigned(token); }}
					onClose={() => { const { onCancel } = attestation; setAttestation(null); onCancel?.(); }}
				/>
			) : null}
		</HeldFilesContext.Provider>
	);
}
